import { useEffect, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { asId } from '@shared/brand';
import type { Holiday } from '@domain/entities/Holiday';
import type { Area, SubArea } from '@domain/entities/Organization';
import {
  EMAIL_EVENTS,
  type EmailEvent,
  type EmailTemplate,
  type RulesConfig,
  type RuntimeConfig,
  type SeniorityConfig,
} from '@domain/entities/RuntimeConfig';
import {
  DEFAULT_FEATURES,
  FEATURE_CATALOG,
  leadsTeams,
  PERMISSION_CATALOG,
  PERMISSION_GROUPS,
  ROLES,
  ROLE_LABEL,
  type FeatureKey,
  type Permission,
  type RejectionScope,
  type Role,
} from '@domain/vocabulary';
import { isProtected, togglePermission, resetRole } from '@domain/policies/RbacPolicy';
import { addBracket, completedYearsOfService, removeBracket } from '@domain/policies/TenurePolicy';
import { EXPIRY_MONTHS_MAX, EXPIRY_MONTHS_MIN } from '@domain/policies/VacationPeriodPolicy';
import { CalendarDate, DayOfWeek } from '@domain/value-objects/CalendarDate';
import { getServices } from '@infrastructure/services';
import {
  Banner,
  Button,
  Card,
  Chip,
  EmptyState,
  Modal,
  Spinner,
  cssVars,
  cx,
  selectClass,
} from '../../design-system';
import { formatDay, MONTHS_LONG, reasonFor, WEEKDAYS_SHORT } from '../../format';
import logoUrl from '../../assets/knLogo.png';
import { useAccess } from '../../hooks/useAccess';
import { usePendingActions } from '../../hooks/usePendingActions';
import { useRuntimeConfig } from '../../providers/RuntimeConfigProvider';
import { useSession } from '../../providers/SessionProvider';
import { useToast } from '../../providers/ToastProvider';
import shared from '../shared.module.css';
import styles from './SystemScreens.module.css';
import { loadFailed } from '../../loadError';
import { orgChartPath, orgChartState } from '../OrgChart/orgChartLinks';
import { AssistantSettingsTab } from './AssistantSettingsTab';
import { PageTitle } from '../../shell/PageTitle';
import { PRODUCT_NAME } from '../../brand';

/**
 * The superuser screens: Áreas, Features y módulos, Feriados, and Configuración
 * global.
 *
 * Every write here goes through `updateRuntimeConfig`, which is superuser-only and
 * audited — a change to the permission matrix or a module switch affects everyone, so
 * it has to leave a trace of who made it.
 */

// ---------------------------------------------------------------------------
// Áreas de la empresa
// ---------------------------------------------------------------------------

interface AreasPanelProps {
  /** El modal de alta lo abre un botón de la cabecera, fuera de este panel. */
  readonly creating: boolean;
  readonly onCloseCreate: () => void;
}

/* Exportado para la prueba de «Eliminar»: es el único sitio del portal donde un
   botón de borrado desactivaba, y lo que fija la prueba es qué llamada sale. */
export const AreasPanel = ({ creating, onCloseCreate }: AreasPanelProps): ReactNode => {
  const config = useRuntimeConfig();
  const toast = useToast();
  const navigate = useNavigate();
  const { holdsAny } = useAccess();
  /* Verlo es por rol y armarlo por persona: el botón sale con cualquiera. */
  const seesOrgChart = holdsAny(['ver_organigrama', 'editar_organigrama']);
  /*
   * Keyed by row rather than by control: while a sub area is being deleted its
   * own capacity steppers are meaningless too, and the three of them acting as
   * one is easier to read than three independent spinners on the same line.
   */
  const action = usePendingActions();
  /*
   * The cache is edited directly and the refetch is fired without being awaited.
   *
   * Awaiting it put a second round trip between the successful write and the row
   * changing on screen — the mutation was already done and the list still showed
   * the old world. Writing the known outcome into the cache makes it immediate;
   * the refetch still runs, so anything the server decided that we did not
   * predict lands a moment later. A failed write puts the snapshot back.
   */
  const queryClient = useQueryClient();
  const areasKey = ['system', 'areas'];
  const subAreasKey = ['system', 'subAreas'];

  const [editing, setEditing] = useState<string | null>(null);
  /* Qué área está a punto de borrarse. Guarda el área entera y no el id: el modal
     dice el nombre, y el nombre tiene que seguir ahí mientras la lista se recarga. */
  const [confirmDelete, setConfirmDelete] = useState<Area | null>(null);
  const [draftAdmin, setDraftAdmin] = useState('');
  const [newName, setNewName] = useState('');
  const [newAdmin, setNewAdmin] = useState('');
  const [newSubAreaName, setNewSubAreaName] = useState('');
  const [newSubAreaMax, setNewSubAreaMax] = useState(2);

  const areas = useQuery({
    queryKey: areasKey,
    queryFn: async () => {
      const result = await getServices().organization.listAreas();
      if (!result.ok) throw loadFailed('No pudimos cargar las áreas.', result.error);
      return result.value;
    },
  });

  const subAreas = useQuery({
    queryKey: subAreasKey,
    enabled: (areas.data ?? []).length > 0,
    queryFn: async () => {
      const results = await Promise.all(
        (areas.data ?? []).map(async (area) => {
          const result = await getServices().organization.listSubAreas(area.id);
          return result.ok ? result.value : [];
        }),
      );
      return results.flat();
    },
  });

  const areaPeople = useQuery({
    queryKey: ['system', 'areaPeopleCounts'],
    enabled: (areas.data ?? []).length > 0,
    queryFn: async () => {
      const entries = await Promise.all(
        (areas.data ?? []).map(async (area) => {
          const result = await getServices().people.list(
            { areaId: area.id, status: 'active' },
            { limit: 200 },
          );
          return [area.id, result.ok ? result.value.items.length : 0] as const;
        }),
      );
      return new Map(entries);
    },
  });

  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(null);
  /*
   * Sin selección no hay área, y antes caía a la primera de la lista.
   *
   * Eso hacía que el panel de sub áreas se abriera siempre enseñando algo que
   * nadie había pedido —y con un rótulo fijo que decía «Tecnología» mirase lo que
   * mirase—. Ahora el panel espera: se elige un área a la izquierda y sus sub
   * áreas salen a la derecha.
   */
  const selectedArea = (areas.data ?? []).find((area) => area.id === selectedAreaId) ?? null;
  const selectedSubAreas = (subAreas.data ?? []).filter(
    (subArea) => subArea.areaId === selectedArea?.id,
  );

  const leaders = useQuery({
    queryKey: ['system', 'subAreaLeaders', selectedSubAreas.map((item) => item.leadPersonId).join(',')],
    enabled: selectedSubAreas.some((item) => item.leadPersonId !== null),
    queryFn: async () => {
      const ids = selectedSubAreas
        .map((item) => item.leadPersonId)
        .filter((id): id is NonNullable<typeof id> => id !== null);
      const result = await getServices().people.getMany(ids);
      return result.ok ? result.value : [];
    },
  });

  const updateSubArea = async (subArea: SubArea, delta: number): Promise<void> => {
    const next = Math.max(0, subArea.maxConcurrentOut + delta);
    const previous = queryClient.getQueryData<readonly SubArea[]>(subAreasKey);

    queryClient.setQueryData<readonly SubArea[]>(subAreasKey, (current) =>
      (current ?? []).map((item) =>
        item.id === subArea.id ? { ...item, maxConcurrentOut: next } : item,
      ),
    );

    const result = await getServices().organization.updateSubArea(subArea.id, {
      maxConcurrentOut: next,
    });
    if (!result.ok) {
      queryClient.setQueryData(subAreasKey, previous);
      toast.bad(`No se pudo actualizar el máximo de personas fuera.${reasonFor(result.error)}`);
      return;
    }
    void subAreas.refetch();
  };

  const removeSubArea = async (subArea: SubArea): Promise<void> => {
    const previous = queryClient.getQueryData<readonly SubArea[]>(subAreasKey);

    // The row goes now. The server can still refuse — it does when people are
    // still assigned — and then the snapshot brings it back with the reason.
    queryClient.setQueryData<readonly SubArea[]>(subAreasKey, (current) =>
      (current ?? []).filter((item) => item.id !== subArea.id),
    );

    const result = await getServices().organization.deleteSubArea(subArea.id);
    if (!result.ok) {
      queryClient.setQueryData(subAreasKey, previous);
      toast.bad(`No se pudo eliminar la sub área.${reasonFor(result.error)}`);
      return;
    }
    toast.ok(`${subArea.name} eliminada.`);
    void subAreas.refetch();
  };

  /*
   * Quién puede administrar el área que se está editando.
   *
   * Se pide sólo al abrir el editor: son hasta 137 personas en un área, y
   * cargarlas para las ocho filas de la lista sería traer el padrón entero para
   * un desplegable que casi nunca se abre.
   *
   * Sin correo no entra: administrar un área exige iniciar sesión, y quien no
   * tiene dirección —los diez de limpieza, chóferes y seguridad— nunca podrá.
   */
  const candidates = useQuery({
    queryKey: ['system', 'areaAdminCandidates', editing],
    enabled: editing !== null,
    queryFn: async () => {
      const result = await getServices().people.list(
        { areaId: asId<'AreaId'>(editing ?? ''), status: 'active' },
        { limit: 200 },
      );
      if (!result.ok) throw loadFailed('No pudimos cargar a las personas del área.', result.error);
      return result.value.items
        .filter((person) => person.email.length > 0)
        .sort((a, b) => {
          /* Los líderes primero: son quienes ya llevan gente y el candidato
             natural. Dentro de cada grupo, por nombre. */
          const rank = (role: string): number => (role === 'lider' ? 0 : role === 'admin' ? 1 : 2);
          const byRole = rank(a.role) - rank(b.role);
          return byRole !== 0 ? byRole : a.displayName.localeCompare(b.displayName, 'es');
        });
    },
  });

  const createSubArea = async (): Promise<void> => {
    if (selectedArea === null || newSubAreaName.trim().length === 0) return;
    const result = await getServices().organization.createSubArea({
      areaId: selectedArea.id,
      name: newSubAreaName.trim(),
      maxConcurrentOut: newSubAreaMax,
    });
    if (!result.ok) {
      toast.bad(`No se pudo crear la sub área.${reasonFor(result.error)}`);
      return;
    }
    toast.ok(`${newSubAreaName.trim()} creada.`);
    setNewSubAreaName('');
    // Not awaited: the id is the server's to assign, so there is nothing to
    // predict — but nothing is gained by holding the button until it arrives.
    void subAreas.refetch();
  };

  /**
   * The allowed domain, shown with a leading `@`.
   *
   * It is stored bare ("knotion.com") because that is what the blocking function
   * compares against; the `@` belongs to how it reads, not to the value.
   */
  const bareDomain = config.identity.allowedDomains[0] ?? 'knotion.com';
  const domain = `@${bareDomain}`;

  const toggleActive = async (id: string, active: boolean, name: string): Promise<void> => {
    const previous = queryClient.getQueryData<readonly Area[]>(areasKey);

    queryClient.setQueryData<readonly Area[]>(areasKey, (current) =>
      (current ?? []).map((area) => (area.id === id ? { ...area, active } : area)),
    );

    const result = await getServices().organization.setAreaActive(asId<'AreaId'>(id), active);
    if (!result.ok) {
      queryClient.setQueryData(areasKey, previous);
      toast.bad(`No se pudo cambiar el estado del área.${reasonFor(result.error)}`);
      return;
    }
    toast.ok(`${name} ${active ? 'activada' : 'desactivada'}.`);
    void areas.refetch();
  };

  /**
   * Borra el área de verdad.
   *
   * «Eliminar» llamaba a `toggleActive(false)`: el botón decía una cosa, hacía
   * otra, y el área seguía en la lista con el chip «Inactiva» al lado del mismo
   * botón que acababa de pulsarse. El interruptor de al lado ya hacía eso, mejor
   * dicho.
   *
   * Aquí no se toca la caché antes de tiempo, al revés que en el resto del panel:
   * el servidor se niega si queda gente o historial colgando, y quitar la fila
   * para devolverla un segundo después con un toast rojo es peor que esperar.
   */
  const removeArea = async (area: Area): Promise<void> => {
    const result = await getServices().organization.deleteArea(area.id);
    if (!result.ok) {
      toast.bad(`No se pudo eliminar el área.${reasonFor(result.error)}`);
      return;
    }
    toast.ok(`${area.name} eliminada.`);
    setConfirmDelete(null);
    /* El panel de la derecha enseña las sub áreas del área seleccionada; si era
       ésta, se queda mirando a un muerto. */
    if (selectedAreaId === area.id) setSelectedAreaId(null);
    void areas.refetch();
    void subAreas.refetch();
    void areaPeople.refetch();
  };

  const saveAdmin = async (id: string, name: string): Promise<void> => {
    if (draftAdmin.length === 0) return;

    const result = await getServices().organization.setAreaAdmin(
      asId<'AreaId'>(id),
      asId<'PersonId'>(draftAdmin),
    );
    if (!result.ok) {
      toast.bad(`No se pudo nombrar administrador.${reasonFor(result.error)}`);
      return;
    }
    const elegido = (candidates.data ?? []).find((person) => person.id === draftAdmin);
    /* Se dice que además cambió el rol: es la mitad de lo que acaba de pasar y la
       que de verdad habilita a alguien. */
    toast.ok(
      `${elegido?.displayName ?? 'La persona'} administra ${name}. Ya tiene el rol de RR.HH.`,
    );
    setEditing(null);
    setDraftAdmin('');
    void areas.refetch();
  };

  /* Devuelve si funcionó: el modal se cierra sólo entonces. Cerrarlo sobre un
     error dejaría el toast rojo detrás y lo escrito perdido. */
  const create = async (): Promise<boolean> => {
    if (newName.trim().length === 0) return false;

    const result = await getServices().organization.createArea({
      name: newName.trim(),
      adminEmail: newAdmin.trim().toLowerCase(),
      color: PALETTE[(areas.data ?? []).length % PALETTE.length] ?? '#4a6fa5',
    });

    if (!result.ok) {
      toast.bad(`No se pudo crear el área.${reasonFor(result.error)}`);
      return false;
    }
    toast.ok(`${newName.trim()} creada. Su administrador cargará sub áreas y personas.`);
    setNewName('');
    setNewAdmin('');
    void areas.refetch();
    return true;
  };

  /* Descartar limpia: reabrir el modal no debe traer de vuelta lo que alguien
     acaba de descartar. Un fallo sí conserva lo escrito — ver `create`. */
  const cancelCreate = (): void => {
    setNewName('');
    setNewAdmin('');
    onCloseCreate();
  };

  const list = areas.data ?? [];
  const leaderName = (subArea: SubArea): string => {
    const leader = leaders.data?.find((person) => person.id === subArea.leadPersonId);
    /* «Responsable» y no «Líder»: responde por la sub área, no firma vacaciones.
       Ver la nota en la pestaña de sub áreas de `AdminScreen`. */
    return leader === undefined ? 'Por asignar' : `Responsable ${leader.displayName}`;
  };

  return (
    <div>
      <div className={styles.split}>
        <section className={styles.panel}>
          <div className={styles.panelHead}>
            <h2 className={cx(styles.panelTitle, styles.panelTitleLarge)}>Áreas activas</h2>
          </div>

          {areas.isLoading ? (
            <Spinner label="Cargando áreas" />
          ) : areas.error !== null ? (
            <Banner tone="danger">No pudimos cargar las áreas.</Banner>
          ) : (
            <ul className={styles.rows}>
              {list.map((area) => (
                <li
                  key={area.id}
                  className={cx(
                    styles.areaRow,
                    styles.areaRowSelectable,
                    area.id === selectedAreaId && styles.areaRowSelected,
                    !area.active && styles.switchRowOff,
                  )}
                  /*
                   * La celda entera selecciona, salvo donde hay un control.
                   *
                   * No es un `<button>` envolvente porque dentro viven el
                   * interruptor, «Eliminar», el correo del admin y su editor: un
                   * botón no puede contener botones, y si pudiera, tocar
                   * cualquiera de ellos seleccionaría el área de paso. Se mira de
                   * dónde salió el clic y se ignora si salió de algo pulsable.
                   *
                   * El teclado sigue entrando por el nombre, que es un botón de
                   * verdad: esto añade una zona cómoda para el ratón, no sustituye
                   * al control accesible.
                   */
                  onClick={(event) => {
                    if ((event.target as HTMLElement).closest('button, input, a, label')) return;
                    setSelectedAreaId(area.id === selectedAreaId ? null : area.id);
                  }}
                >
                  <span
                    className={styles.colorDot}
                    style={cssVars({ '--dot': area.color })}
                    aria-hidden="true"
                  />
                  {/*
                    Selecciona el bloque de texto y no la fila entera: la fila
                    lleva dentro un interruptor, «Eliminar», el correo del admin y
                    su botón de edición, y un clicable que los contenga a todos
                    dispara la selección cada vez que alguien toca cualquiera.
                  */}
                  <button
                    type="button"
                    className={cx(styles.areaText, styles.areaSelect)}
                    aria-pressed={area.id === selectedAreaId}
                    onClick={() => {
                      setSelectedAreaId(area.id === selectedAreaId ? null : area.id);
                    }}
                  >
                    <div className={styles.areaName}>{area.name}</div>
                    <div className={styles.areaDetail}>
                      {(subAreas.data ?? []).filter((item) => item.areaId === area.id).length}{' '}
                      {(subAreas.data ?? []).filter((item) => item.areaId === area.id).length === 1
                        ? 'sub área'
                        : 'sub áreas'}{' '}
                      · {areaPeople.data?.get(area.id) ?? 0}{' '}
                      {areaPeople.data?.get(area.id) === 1 ? 'persona' : 'personas'}
                    </div>
                  </button>

                  <Chip
                    background={area.active ? 'var(--color-success-bg)' : 'var(--color-hairline)'}
                    foreground={
                      area.active ? 'var(--color-success-ink)' : 'var(--color-text-tertiary-alt)'
                    }
                  >
                    {area.active ? 'Activa' : 'Inactiva'}
                  </Chip>

                  <Toggle
                    on={area.active}
                    disabled={action.isPending(`area:${area.id}`)}
                    label={`${area.active ? 'Desactivar' : 'Activar'} ${area.name}`}
                    onToggle={() => {
                      action.run(`area:${area.id}`, () =>
                        toggleActive(area.id, !area.active, area.name),
                      );
                    }}
                  />

                  {/* Un área inactiva también se elimina —es el caso más común:
                      se desactivó primero y luego se quiso quitar—, así que el
                      botón ya no depende de `active`. Lo que sí hace es preguntar. */}
                  <button
                    type="button"
                    className={cx(
                      styles.removeArea,
                      action.isPending(`area:${area.id}`) && styles.pending,
                    )}
                    disabled={action.isPending(`area:${area.id}`)}
                    onClick={() => {
                      setConfirmDelete(area);
                    }}
                  >
                    Eliminar
                  </button>

                  <div className={styles.areaAdminBar}>
                    <Chip
                      background={
                        area.adminEmail.length > 0
                          ? 'var(--color-brand-bg)'
                          : 'var(--color-warning-bg)'
                      }
                      foreground={
                        area.adminEmail.length > 0
                          ? 'var(--color-brand-ink)'
                          : 'var(--color-warning-ink)'
                      }
                    >
                      {area.adminEmail.length > 0 ? 'Admin del área' : 'Sin admin'}
                    </Chip>

                    {editing === area.id ? (
                      <span className={styles.areaEditRow}>
                        {/*
                          De la lista y no a mano. Un correo tecleado sólo tenía
                          que acabar en el dominio: podía apuntar a alguien que no
                          trabaja aquí, y el área se quedaba igual de huérfana pero
                          sin el aviso de «Sin admin».
                        */}
                        <select
                          className={styles.areaEditInput}
                          aria-label={`Administrador de ${area.name}`}
                          value={draftAdmin}
                          disabled={candidates.isPending}
                          onChange={(event) => {
                            setDraftAdmin(event.target.value);
                          }}
                        >
                          <option value="">
                            {candidates.isPending ? 'Cargando personas…' : 'Elige a quién'}
                          </option>
                          {(candidates.data ?? []).map((person) => (
                            <option key={person.id} value={person.id}>
                              {person.displayName}
                              {leadsTeams(person.role) ? ' · líder' : ''}
                              {person.role === 'admin' ? ' · ya es RR.HH.' : ''}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className={cx(
                            styles.saveButton,
                            action.isPending(`admin:${area.id}`) && styles.pending,
                          )}
                          disabled={action.isPending(`admin:${area.id}`) || draftAdmin.length === 0}
                          onClick={() => {
                            action.run(`admin:${area.id}`, () => saveAdmin(area.id, area.name));
                          }}
                        >
                          {action.isPending(`admin:${area.id}`) ? 'Guardando…' : 'Guardar'}
                        </button>
                        <button
                          type="button"
                          className={styles.cancelButton}
                          onClick={() => {
                            setEditing(null);
                          }}
                        >
                          Cancelar
                        </button>
                        <span className={styles.areaEditHint}>
                          {/* Se dice antes de pulsar: cambia los permisos de una
                              persona, y eso no debería enterarse nadie después. */}
                          Quien elijas pasa a tener el rol de RR.HH. sobre {area.name}. Al
                          administrador anterior no se le quita nada.
                        </span>
                      </span>
                    ) : (
                      <span className={styles.areaEditRow}>
                        <span
                          className={cx(
                            styles.areaAdminMail,
                            area.adminEmail.length === 0 && styles.areaAdminMissing,
                          )}
                        >
                          {area.adminEmail.length > 0 ? area.adminEmail : 'Falta asignarlo'}
                        </span>
                        <button
                          type="button"
                          className={styles.inlineButton}
                          onClick={() => {
                            setEditing(area.id);
                            setDraftAdmin('');
                          }}
                        >
                          {area.adminEmail.length > 0 ? 'Cambiar admin' : 'Asignar admin'}
                        </button>
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/*
          Crear un área es un acto raro y con consecuencias —nace vacía y alguien
          tiene que llenarla—, y ocupaba una tarjeta permanente en la columna
          derecha de una pantalla que se abre a diario para otras cosas. Pasa a un
          modal que abre un botón de la cabecera; lo que queda en la columna es lo
          que sí se consulta.
        */}
        {creating && (
          <Modal
            title="Crear área padre"
            onClose={cancelCreate}
            footer={
              <>
                <Button variant="ghost" onClick={cancelCreate}>
                  Cancelar
                </Button>
                <Button
                  variant="primary"
                  disabled={action.isPending('createArea') || newName.trim().length === 0}
                  onClick={() => {
                    action.run('createArea', async () => {
                      if (await create()) onCloseCreate();
                    });
                  }}
                >
                  {action.isPending('createArea') ? 'Creando…' : 'Crear área'}
                </Button>
              </>
            }
          >
            <div className={styles.stackedForm}>
              <Banner tone="info">
                Al crearla queda vacía: su administrador cargará sub áreas y personas.
              </Banner>
              <label className={cx(styles.field, styles.fieldBlock)}>
                <span className={styles.fieldLabel}>Nombre del área</span>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="Ej. Creación de contenidos"
                  value={newName}
                  onChange={(event) => {
                    setNewName(event.target.value);
                  }}
                />
              </label>
              <label className={cx(styles.field, styles.fieldBlock)}>
                <span className={styles.fieldLabel}>Correo del administrador del área</span>
                <input
                  type="text"
                  className={styles.input}
                  placeholder={`nombre${domain}`}
                  value={newAdmin}
                  onChange={(event) => {
                    setNewAdmin(event.target.value);
                  }}
                />
              </label>
            </div>
          </Modal>
        )}

        {/*
          Preguntar antes, y decir qué se lleva por delante.

          Borrar un área no se deshace, y el botón vive a un centímetro del
          interruptor de activar: sin esta pantalla intermedia, un clic torcido se
          lleva la estructura de un área entera. Se enseñan las dos cifras que
          deciden si es seguro —sus sub áreas se van con ella; su gente la
          bloquea— y no se adelanta el resultado: el servidor tiene la última
          palabra y puede contar bajas que esta pantalla no enseña.
        */}
        {confirmDelete !== null && (
          <Modal
            title={`Eliminar ${confirmDelete.name}`}
            onClose={() => {
              setConfirmDelete(null);
            }}
            footer={
              <>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setConfirmDelete(null);
                  }}
                >
                  Conservar
                </Button>
                <Button
                  variant="danger"
                  busy={action.isPending(`delete:${confirmDelete.id}`)}
                  onClick={() => {
                    const area = confirmDelete;
                    action.run(`delete:${area.id}`, () => removeArea(area));
                  }}
                >
                  Eliminar el área
                </Button>
              </>
            }
          >
            <div className={styles.stackedForm}>
              <p className={styles.panelNote}>
                Se borra {confirmDelete.name} y no hay forma de recuperarla. Si sólo quieres que
                deje de usarse, el interruptor de la fila la desactiva y la conserva.
              </p>
              <Banner tone={areaPeople.data?.get(confirmDelete.id) === 0 ? 'info' : 'danger'}>
                {(() => {
                  const subs = (subAreas.data ?? []).filter(
                    (item) => item.areaId === confirmDelete.id,
                  ).length;
                  const gente = areaPeople.data?.get(confirmDelete.id) ?? 0;
                  if (gente > 0) {
                    return `Tiene ${String(gente)} ${gente === 1 ? 'persona activa' : 'personas activas'}. Muévelas a otra área antes: el servidor no la va a eliminar así.`;
                  }
                  return subs === 0
                    ? 'No tiene sub áreas ni personas activas.'
                    : `Se ${subs === 1 ? 'va' : 'van'} con ella ${String(subs)} ${subs === 1 ? 'sub área' : 'sub áreas'}. Si alguien sigue asignado —incluidas las bajas— o hay solicitudes en su historial, el servidor lo va a impedir.`;
                })()}
              </Banner>
            </div>
          </Modal>
        )}

        <div className={styles.column}>
          {/*
            El panel espera a que se elija un área. Tenía su propia fila de chips
            para escogerla, que era un segundo selector de lo mismo: dos sitios
            donde elegir área, capaces de discrepar, en una pantalla cuya mitad
            izquierda ya es la lista de áreas.
          */}
          {selectedArea === null ? (
            <section className={styles.panel}>
              <EmptyState
                glyph="·"
                title="Elige un área"
                note="Sus sub áreas y cuántas personas pueden estar fuera a la vez en cada una salen aquí."
              />
            </section>
          ) : (
          <section className={styles.panel}>
            <div className={styles.panelHead}>
              <div className={styles.panelHeadText}>
                <h2 className={styles.panelTitle}>Sub áreas de {selectedArea.name}</h2>
                <p className={styles.panelNote}>
                  Cuántas personas pueden estar fuera al mismo tiempo en cada una. Su
                  administrador las ve al instante.
                </p>
              </div>
              <Chip background="var(--color-violet-bg)" foreground="var(--color-violet-ink)">
                {selectedSubAreas.length}{' '}
                {selectedSubAreas.length === 1 ? 'sub área' : 'sub áreas'}
              </Chip>
              {seesOrgChart && (
                <Button
                  variant="secondary"
                  small
                  onClick={() => {
                    void navigate(orgChartPath(selectedArea.id), {
                      state: orgChartState({ path: '/areas', label: 'Áreas de la empresa' }),
                    });
                  }}
                >
                  Organigrama
                </Button>
              )}
            </div>

            {/* Sin esto el panel salta del título al formulario, y no se distingue
                un área sin sub áreas de una que todavía está cargando. */}
            {selectedSubAreas.length === 0 && (
              <p className={styles.panelNote}>
                {selectedArea.name} todavía no tiene sub áreas. Crea la primera abajo.
              </p>
            )}

            <div className={styles.subAreaRows}>
              {selectedSubAreas.map((subArea) => {
                const rowKey = `subArea:${subArea.id}`;
                const rowBusy = action.isPending(rowKey);

                return (
                <div key={subArea.id} className={styles.subAreaRow}>
                  <span
                    className={styles.colorDot}
                    style={cssVars({ '--dot': subArea.color })}
                    aria-hidden="true"
                  />
                  <span className={styles.subAreaText}>
                    <span className={styles.subAreaName}>{subArea.name}</span>
                    {/* Envuelto para poder dejar que el nombre parta: el chip del
                        sistema de diseño no parte, y aquí dentro hay nombres de
                        cuarenta caracteres. Ver `.subAreaLead`. */}
                    <span className={styles.subAreaLead}>
                      <Chip background="var(--color-brand-bg)" foreground="var(--color-brand-ink)">
                        {leaderName(subArea)}
                      </Chip>
                    </span>
                  </span>
                  <span className={styles.subAreaCap}>
                    <span>Máx. fuera</span>
                    <span className={cx(styles.stepperCompact, rowBusy && styles.pending)}>
                      <button
                        type="button"
                        aria-label={`Reducir máximo de ${subArea.name}`}
                        disabled={rowBusy}
                        onClick={() => {
                          action.run(rowKey, () => updateSubArea(subArea, -1));
                        }}
                      >
                        −
                      </button>
                      <strong>{subArea.maxConcurrentOut}</strong>
                      <button
                        type="button"
                        aria-label={`Aumentar máximo de ${subArea.name}`}
                        disabled={rowBusy}
                        onClick={() => {
                          action.run(rowKey, () => updateSubArea(subArea, 1));
                        }}
                      >
                        +
                      </button>
                    </span>
                  </span>
                  <button
                    type="button"
                    className={cx(styles.removeSubArea, rowBusy && styles.pending)}
                    disabled={rowBusy}
                    onClick={() => {
                      action.run(rowKey, () => removeSubArea(subArea));
                    }}
                  >
                    {rowBusy ? 'Guardando…' : 'Eliminar'}
                  </button>
                </div>
                );
              })}
            </div>

            <div className={styles.subAreaAdd}>
              <label className={styles.addField}>
                <span className={styles.addLabel}>Nueva sub área en {selectedArea.name}</span>
                <input
                  type="text"
                  className={styles.addInput}
                  placeholder="Ej. Data Engineering"
                  value={newSubAreaName}
                  onChange={(event) => {
                    setNewSubAreaName(event.target.value);
                  }}
                />
              </label>
              <label className={styles.subAreaMaxField}>
                <span className={styles.addLabel}>Máx. fuera</span>
                <input
                  type="number"
                  className={styles.addInput}
                  min={0}
                  value={newSubAreaMax}
                  onChange={(event) => {
                    setNewSubAreaMax(Math.max(0, Number(event.target.value) || 0));
                  }}
                />
              </label>
              <button
                type="button"
                className={cx(
                  styles.addSubmit,
                  action.isPending('createSubArea') && styles.pending,
                )}
                disabled={action.isPending('createSubArea')}
                onClick={() => {
                  action.run('createSubArea', createSubArea);
                }}
              >
                {action.isPending('createSubArea') ? 'Agregando…' : 'Agregar'}
              </button>
            </div>
          </section>
          )}

          <div className={styles.scopePanel}>
            <div className={styles.scopeLabel}>Cómo se reparte</div>
            <p className={styles.scopeText}>
              Tú creas y desactivas áreas padre y las features globales. Cada administrador de
              área gestiona sus sub áreas, líderes y cuentas dentro de su propio ámbito.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Accent colours handed to new areas in rotation, so one is renderable at once. */
const PALETTE = ['#4a6fa5', '#12a594', '#7c5cff', '#e8930c', '#e5484d', '#64748b'] as const;

/**
 * A toggle switch.
 *
 * A real `<button role="switch">` rather than a styled checkbox: the design draws
 * a track and a knob, and `aria-checked` is what makes that shape announce itself
 * correctly.
 */
const Toggle = ({
  on,
  label,
  disabled = false,
  color,
  onToggle,
}: {
  on: boolean;
  label: string;
  disabled?: boolean;
  color?: string;
  onToggle: () => void;
}): ReactNode => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={disabled}
    className={cx(styles.toggle, on && styles.toggleOn)}
    style={color === undefined ? undefined : cssVars({ '--toggle-color': color })}
    onClick={onToggle}
  >
    <span className={cx(styles.toggleKnob, on && styles.toggleKnobOn)} />
  </button>
);

// ---------------------------------------------------------------------------
// Features y módulos
// ---------------------------------------------------------------------------

/**
 * The OTA switchboard.
 *
 * Turning a module off here removes its section from every open session's
 * navigation without a redeploy — that is the whole point of keeping feature
 * flags in Firestore rather than in the bundle. A dependent feature is disabled
 * and greyed out whenever its parent is off, because IMSS validation with the
 * incapacity module hidden would offer a control for an unreachable screen.
 */
const FeaturesPanel = (): ReactNode => {
  const config = useRuntimeConfig();
  const toast = useToast();
  /*
   * One key for the board, replacing a per-feature `busy` flag.
   *
   * The old flag disabled only the switch being saved, which read well and was
   * unsound: every write here sends the whole `featureFlags` document, so a
   * second switch flipped mid-save would write the first one's pre-edit value
   * back and the module would quietly turn itself on again.
   */
  const action = usePendingActions();
  const saving = action.busy;

  const isOn = (key: FeatureKey): boolean => config.features[key] === true;

  const toggle = async (key: FeatureKey): Promise<void> => {
    const next = { ...config.features, [key]: !config.features[key] };

    // Turning off a parent must turn off its dependants, or the stored state
    // would claim IMSS validation is on while its module is gone.
    if (key === 'incapacidades' && next.incapacidades === false) next.imss = false;

    const result = await getServices().config.save({ features: next });

    if (!result.ok) {
      toast.bad(`No se pudo guardar el cambio.${reasonFor(result.error)}`);
      return;
    }

    const descriptor = FEATURE_CATALOG.find((f) => f.key === key);
    toast.ok(
      `${descriptor?.label ?? key} ${next[key] ? 'encendido' : 'apagado'} para toda la empresa.`,
    );
  };

  /*
   * Lo que «activar todas» enciende: todo **menos lo que nace apagado**.
   *
   * Kn·Blog y el asistente de WhatsApp nacen apagados porque encenderlos es una
   * decisión —uno sale primero en el menú de toda la empresa, el otro abre un
   * canal por el que se piden y se aprueban vacaciones—. Un botón que enciende
   * todo de golpe no es esa decisión: cada uno tiene su interruptor aquí mismo.
   */
  const bulk = FEATURE_CATALOG.filter((feature) => DEFAULT_FEATURES[feature.key]);

  const enableAll = async (): Promise<void> => {
    const next = { ...config.features };
    for (const feature of bulk) next[feature.key] = true;

    const result = await getServices().config.save({ features: next });
    if (!result.ok) {
      toast.bad(`No se pudo guardar el cambio.${reasonFor(result.error)}`);
      return;
    }
    toast.ok(
      bulk.length === FEATURE_CATALOG.length
        ? 'Todas las capacidades quedaron encendidas.'
        : 'Las capacidades quedaron encendidas. Las que nacen apagadas se encienden una por una.',
    );
  };

  const groups = ['Módulos', 'Gestión', 'Reglas'] as const;
  const onCount = FEATURE_CATALOG.filter((f) => isOn(f.key)).length;

  return (
    <div>
      <div className={styles.panelBar}>
        <Chip background="var(--color-violet-bg)" foreground="var(--color-violet-ink)">
          {onCount} de {FEATURE_CATALOG.length} activas
        </Chip>
        <button
          type="button"
          className={cx(styles.ghostButton, saving && styles.pending)}
          disabled={bulk.every((feature) => isOn(feature.key)) || saving}
          onClick={() => {
            action.run('features', enableAll);
          }}
        >
          {saving ? 'Activando…' : 'Activar todo'}
        </button>
      </div>

      <div className={cx(styles.split, styles.featureSplit)}>
        {groups.map((group) => {
          const items = FEATURE_CATALOG.filter((f) => f.group === group);
          return (
            <section key={group} className={styles.panel}>
              <div className={styles.panelHead}>
                <h2 className={styles.panelTitle}>{group}</h2>
                <Chip background="var(--color-surface-subtle-alt)" foreground="var(--color-brand-ink)">
                  {items.filter((f) => isOn(f.key)).length}/{items.length}
                </Chip>
              </div>

              <ul className={styles.rows}>
                {items.map((feature) => {
                  const parentOff =
                    feature.dependsOn !== undefined && !isOn(feature.dependsOn);
                  const on = isOn(feature.key) && !parentOff;
                  const parent = FEATURE_CATALOG.find((f) => f.key === feature.dependsOn);

                  return (
                    <li
                      key={feature.key}
                      className={cx(styles.switchRow, !on && styles.switchRowOff)}
                    >
                      <span
                        className={styles.colorDot}
                        style={cssVars({ '--dot': feature.color })}
                        aria-hidden="true"
                      />
                      <span className={styles.rowText}>
                        <span className={styles.rowTitleLine}>
                          <span className={styles.rowTitleLarge}>{feature.label}</span>
                          {parent !== undefined && (
                            <Chip
                              background="var(--color-surface-subtle-alt)"
                              foreground="var(--color-text-tertiary-alt)"
                            >
                              Requiere {parent.label}
                            </Chip>
                          )}
                        </span>
                        <span className={styles.rowNote}>{feature.note}</span>
                      </span>

                      <Toggle
                        on={on}
                        color={feature.color}
                        disabled={parentOff || saving}
                        label={`${on ? 'Apagar' : 'Encender'} ${feature.label}`}
                        onToggle={() => {
                          action.run('features', () => toggle(feature.key));
                        }}
                      />
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>

      <div className={cx(styles.scopePanel, styles.scopePanelSpaced)}>
        <div className={styles.scopeLabel}>Alcance del cambio</div>
        <p className={styles.scopeText}>
          Un interruptor de aquí llega en vivo a cada sesión abierta, en todas las áreas: la
          sección desaparece de la navegación sin que nadie recargue ni se redespliegue nada. El
          historial no se toca — apagar un módulo lo oculta, no lo borra.
        </p>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Feriados del país
// ---------------------------------------------------------------------------

/**
 * The holiday calendar.
 *
 * Switching a day off stops it being deducted from every business-day calculation
 * immediately, for everyone — which is exactly why holidays are data rather than a
 * bundled table.
 */
/**
 * Feriados del país.
 *
 * Grouped by month rather than by kind, because the question this screen answers
 * is "when do they fall" — a long weekend in April and a Tuesday in November are
 * planned around differently, and the statutory/customary split is a property of
 * each day, shown on its own chip.
 *
 * Switching a day off returns it to the business-day count for every open
 * session at once, which is why only the superuser holds `editar_feriados`.
 */
const HolidaysPanel = (): ReactNode => {
  const toast = useToast();
  const { holds } = useAccess();
  const action = usePendingActions();
  // Same reasoning as the areas panel: the day changes on screen when the write
  // succeeds, not one round trip later.
  const queryClient = useQueryClient();
  const [year, setYear] = useState(() => getServices().clock.today().year);
  const [draft, setDraft] = useState<{
    date: string;
    name: string;
    type: 'Obligatorio' | 'Por costumbre';
  }>({
    date: '',
    name: '',
    type: 'Obligatorio',
  });

  const editable = holds('editar_feriados');

  const holidaysKey = ['system', 'holidays', year];

  const holidays = useQuery({
    queryKey: holidaysKey,
    queryFn: async () => {
      const result = await getServices().holidays.listForYear(year);
      if (!result.ok) throw loadFailed('No pudimos cargar el calendario.', result.error);
      return result.value;
    },
  });

  /* Counts for the year tabs — each year's own load, not the open one's. */
  const years = [year - 1, year, year + 1];
  const yearCounts = useQuery({
    queryKey: ['system', 'holidayCounts', years.join(',')],
    queryFn: async () => {
      const services = getServices();
      const entries = await Promise.all(
        years.map(async (option) => {
          const result = await services.holidays.listForYear(option);
          return [option, result.ok ? result.value.length : 0] as const;
        }),
      );
      return new Map(entries);
    },
  });

  const list = holidays.data ?? [];
  const active = list.filter((h) => h.enabled);

  const toggle = async (id: string, enabled: boolean, name: string): Promise<void> => {
    const previous = queryClient.getQueryData<readonly Holiday[]>(holidaysKey);

    queryClient.setQueryData<readonly Holiday[]>(holidaysKey, (current) =>
      (current ?? []).map((day) => (day.id === id ? { ...day, enabled } : day)),
    );

    const result = await getServices().holidays.setEnabled(asId<'HolidayId'>(id), enabled);
    if (!result.ok) {
      queryClient.setQueryData(holidaysKey, previous);
      toast.bad(`No se pudo actualizar el feriado.${reasonFor(result.error)}`);
      return;
    }
    toast.ok(`${name} ${enabled ? 'cuenta' : 'ya no cuenta'} como día no laborable.`);
    void holidays.refetch();
  };

  const drop = async (id: string, name: string): Promise<void> => {
    const previous = queryClient.getQueryData<readonly Holiday[]>(holidaysKey);

    queryClient.setQueryData<readonly Holiday[]>(holidaysKey, (current) =>
      (current ?? []).filter((day) => day.id !== id),
    );

    const result = await getServices().holidays.remove(asId<'HolidayId'>(id));
    if (!result.ok) {
      queryClient.setQueryData(holidaysKey, previous);
      toast.bad(`No se pudo eliminar el feriado.${reasonFor(result.error)}`);
      return;
    }
    toast.warn(`${name} eliminado. Se quitó del calendario ${String(year)} para todas las áreas.`);
    void holidays.refetch();
    void yearCounts.refetch();
  };

  const seed = async (): Promise<void> => {
    const result = await getServices().holidays.seedYear(year, true);
    if (!result.ok) {
      toast.bad(`No se pudo generar la plantilla.${reasonFor(result.error)}`);
      return;
    }
    toast.ok(
      `Plantilla LFT ${String(year)} generada. ${String(result.value)} días cargados; agrega los que otorgue la empresa.`,
    );
    void holidays.refetch();
    void yearCounts.refetch();
  };

  const add = async (): Promise<void> => {
    if (draft.date.length === 0) {
      toast.warn('Falta la fecha: elige el día que quieres marcar como feriado.');
      return;
    }
    const date = CalendarDate.fromISO(draft.date);
    if (!date.ok) {
      toast.warn('La fecha no es válida.');
      return;
    }
    if (draft.name.trim().length === 0) {
      toast.warn('Falta el nombre: escribe cómo se llama ese día.');
      return;
    }
    if (list.some((h) => h.date.toISO() === draft.date)) {
      toast.warn(`Ese día ya existe: ya hay un feriado el ${formatDay(date.value)}.`);
      return;
    }

    const result = await getServices().holidays.add({
      date: date.value,
      name: draft.name.trim(),
      type: draft.type,
    });
    if (!result.ok) {
      toast.bad(`No se pudo agregar el feriado.${reasonFor(result.error)}`);
      return;
    }
    toast.ok(
      `${draft.name.trim()} agregado. ${formatDay(date.value)} · el cambio quedó en la bitácora.`,
    );
    setDraft({ date: '', name: '', type: draft.type });
    void holidays.refetch();
    void yearCounts.refetch();
  };

  /*
   * The three figures the design leads with. «Caen en día hábil» is the one that
   * matters for planning: a holiday on a Saturday costs the company nothing and
   * gives the person nothing.
   */
  const stats: readonly { label: string; value: number; note: string; color: string }[] = [
    {
      label: 'Feriados activos',
      value: active.length,
      note: `de ${String(list.length)} del calendario cargado`,
      color: 'var(--color-brand)',
    },
    {
      label: 'Caen en día hábil',
      value: active.filter((h) => !h.date.isWeekend).length,
      note: 'no se descuentan de tus vacaciones',
      color: 'var(--color-success)',
    },
    {
      label: 'Fines de semana largos',
      value: active.filter(
        (h) => h.date.dayOfWeek === DayOfWeek.Monday || h.date.dayOfWeek === DayOfWeek.Friday,
      ).length,
      note: 'feriados pegados al fin de semana',
      color: 'var(--color-warning)',
    },
  ];

  const months = MONTHS_LONG.map((month, index) => ({
    month,
    index,
    items: list
      .filter((h) => h.date.month === index + 1)
      .sort((a, b) => a.date.day - b.date.day),
  })).filter((group) => group.items.length > 0);

  return (
    <div className={shared.screen}>
      <div className={styles.ferHead}>
        <div className={cx(styles.configContextSmall, styles.ferContextText)}>
          <h1 className={styles.configContextTitle}>Feriados del país</h1>
          <p className={styles.note}>
            México · descansos obligatorios de la LFT · 2026. Los feriados activos no se descuentan de tus días de vacaciones.
          </p>
        </div>
        <div className={styles.ferStats}>
          {stats.map((stat) => (
            <div
              key={stat.label}
              className={styles.ferStat}
              style={cssVars({ '--fer-color': stat.color })}
            >
              <span className={styles.ferStatValue}>{stat.value}</span>
              <span className={styles.ferStatLabel}>{stat.label}</span>
            </div>
          ))}
        </div>
      </div>

      <section className={styles.panel}>
        <div className={styles.panelHead}>
          <div className={styles.panelHeadText}>
            <h2 className={styles.panelTitle}>Calendario {year}</h2>
            <p className={styles.panelNote}>
              {list.length === 0
                ? 'Sin días cargados. Genera la plantilla y ajusta lo que aplique a la empresa.'
                : `${String(list.length)} días cargados · ${String(
                    active.length,
                  )} activos. Solo el superusuario edita este calendario.`}
            </p>
          </div>
          <div className={styles.tabTrack} role="tablist" aria-label="Año del calendario">
            {years.map((option) => (
              <button
                key={option}
                type="button"
                role="tab"
                aria-selected={option === year}
                className={cx(styles.tab, option === year && styles.tabActive)}
                onClick={() => {
                  setYear(option);
                }}
              >
                {option} · {yearCounts.data?.get(option) ?? 0}
              </button>
            ))}
          </div>
        </div>

        {list.length === 0 ? (
          <div className={styles.ferEmpty}>
            <EmptyState
              glyph="★"
              title={`Aún no hay días cargados para ${String(year)}`}
              note="Genera los descansos obligatorios de la LFT y después agrega los días por costumbre de la empresa."
            />
            {editable && (
              <button
                type="button"
                className={cx(styles.ferSeed, action.isPending('seed') && styles.pending)}
                disabled={action.isPending('seed')}
                onClick={() => {
                  action.run('seed', seed);
                }}
              >
                {action.isPending('seed') ? 'Generando…' : 'Generar plantilla LFT'}
              </button>
            )}
          </div>
        ) : (
          editable && (
            <div className={styles.ferForm}>
              <label className={styles.tierField}>
                <span className={styles.tierFieldLabel}>Fecha</span>
                <input
                  type="date"
                  className={styles.tierInput}
                  min={`${String(year)}-01-01`}
                  max={`${String(year)}-12-31`}
                  value={draft.date}
                  onChange={(event) => {
                    setDraft((d) => ({ ...d, date: event.target.value }));
                  }}
                />
              </label>
              <label className={cx(styles.tierField, styles.ferNameField)}>
                <span className={styles.tierFieldLabel}>Nombre del día</span>
                <input
                  type="text"
                  className={styles.tierInput}
                  placeholder="Aniversario de la empresa"
                  value={draft.name}
                  onChange={(event) => {
                    setDraft((d) => ({ ...d, name: event.target.value }));
                  }}
                />
              </label>
              <label className={styles.tierField}>
                <span className={styles.tierFieldLabel}>Tipo</span>
                <select
                  className={selectClass()}
                  value={draft.type}
                  onChange={(event) => {
                    setDraft((d) => ({
                      ...d,
                      type: event.target.value === 'Por costumbre' ? 'Por costumbre' : 'Obligatorio',
                    }));
                  }}
                >
                  <option value="Obligatorio">Descanso obligatorio</option>
                  <option value="Por costumbre">Por costumbre</option>
                </select>
              </label>
              <button
                type="button"
                className={cx(styles.tierAdd, action.isPending('addHoliday') && styles.pending)}
                disabled={action.isPending('addHoliday')}
                onClick={() => {
                  action.run('addHoliday', add);
                }}
              >
                {action.isPending('addHoliday') ? 'Agregando…' : '+ Agregar feriado'}
              </button>
            </div>
          )
        )}
      </section>

      {holidays.isLoading ? (
        <Card>
          <Spinner label="Cargando feriados" />
        </Card>
      ) : (
        <div className={styles.ferMonths}>
          {months.map((group) => (
            <section key={group.month} className={styles.panel}>
              <div className={styles.panelHead}>
                <h3 className={styles.ferMonthTitle}>{group.month}</h3>
                <Chip background="var(--color-brand-bg-alt)" foreground="var(--color-brand-ink)">
                  {group.items.length} {group.items.length === 1 ? 'feriado' : 'feriados'}
                </Chip>
              </div>

              <ul className={styles.ferList}>
                {group.items.map((holiday) => (
                  <HolidayCard
                    key={holiday.id}
                    holiday={holiday}
                    editable={editable}
                    busy={action.isPending(`holiday:${holiday.id}`)}
                    onToggle={(id, enabled, name) => {
                      action.run(`holiday:${id}`, () => toggle(id, enabled, name));
                    }}
                    onRemove={(id, name) => {
                      action.run(`holiday:${id}`, () => drop(id, name));
                    }}
                  />
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * One day.
 *
 * The bridge chip is the reason the weekday is shown at all: a Tuesday holiday
 * invites a Monday off, a Thursday invites a Friday, and knowing which is what
 * a planner is actually looking for.
 */
const HolidayCard = ({
  holiday,
  editable,
  busy,
  onToggle,
  onRemove,
}: {
  holiday: Holiday;
  editable: boolean;
  /** This day's own mutation is in flight. Both of its controls stand down. */
  busy: boolean;
  onToggle: (id: string, enabled: boolean, name: string) => void;
  onRemove: (id: string, name: string) => void;
}): ReactNode => {
  const weekend = holiday.date.isWeekend;
  const bridge = weekend
    ? ''
    : holiday.date.dayOfWeek === DayOfWeek.Tuesday
      ? 'Puente con el lunes'
      : holiday.date.dayOfWeek === DayOfWeek.Thursday
        ? 'Puente con el viernes'
        : holiday.date.dayOfWeek === DayOfWeek.Monday ||
            holiday.date.dayOfWeek === DayOfWeek.Friday
          ? 'Fin de semana largo'
          : '';

  return (
    <li className={cx(styles.ferCard, !holiday.enabled && styles.ferCardOff)}>
      <div className={styles.ferCardHead}>
        <span className={cx(styles.ferDate, weekend && styles.ferDateWeekend)} aria-hidden="true">
          <span className={styles.ferDow}>{WEEKDAYS_SHORT[holiday.date.dayOfWeek]}</span>
          <span className={styles.ferDay}>{holiday.date.day}</span>
        </span>
        <span className={styles.ferCardText}>
          <span className={cx(styles.ferName, !holiday.enabled && styles.ferNameOff)}>
            {holiday.name}
          </span>
          <Chip
            background={
              weekend
                ? 'var(--color-surface-subtle-alt)'
                : holiday.type === 'Obligatorio'
                  ? 'var(--color-success-bg)'
                  : 'var(--color-brand-bg-alt)'
            }
            foreground={
              weekend
                ? 'var(--color-text-tertiary-alt)'
                : holiday.type === 'Obligatorio'
                  ? 'var(--color-success-ink)'
                  : 'var(--color-brand-ink)'
            }
          >
            {weekend
              ? 'Cae en fin de semana'
              : holiday.type === 'Obligatorio'
                ? 'Descanso obligatorio'
                : 'Por costumbre'}
          </Chip>
        </span>
      </div>

      <div className={styles.ferCardFoot}>
        {bridge.length > 0 && (
          <Chip background="var(--color-warning-bg)" foreground="var(--color-warning-ink)">
            {bridge}
          </Chip>
        )}
        <button
          type="button"
          className={cx(
            styles.ferState,
            holiday.enabled ? styles.ferStateOn : styles.ferStateOff,
            busy && styles.pending,
          )}
          disabled={!editable || busy}
          onClick={() => {
            onToggle(holiday.id, !holiday.enabled, holiday.name);
          }}
        >
          {busy ? 'Guardando…' : holiday.enabled ? 'Activo' : 'Inactivo'}
        </button>
        {editable && (
          <button
            type="button"
            className={cx(styles.ferRemove, busy && styles.pending)}
            disabled={busy}
            aria-label={`Eliminar ${holiday.name}`}
            onClick={() => {
              onRemove(holiday.id, holiday.name);
            }}
          >
            ×
          </button>
        )}
      </div>
    </li>
  );
};


// ---------------------------------------------------------------------------
// Configuración global
// ---------------------------------------------------------------------------

/**
 * Configuración de la empresa — transcribed from
 * `design/reference/screens/configuracion-de-la-empresa.html`.
 *
 * One destination, not four. The prototype gathers Identidad, Áreas, Feriados,
 * Reglas, Roles, Catálogos, Features and Correos behind a single sidebar entry
 * with grouped tab tracks, and the header's title and subtitle change with the
 * active tab (`cfgTitle` / `cfgSubtitle`). Promoting these to separate sidebar
 * items gives a superuser a «Sistema» group the design never shows.
 */

type ConfigTab =
  | 'identidad'
  | 'areas'
  | 'feriados'
  | 'reglas'
  | 'roles'
  | 'catalogos'
  | 'features'
  | 'correos'
  | 'asistente';

interface ConfigTabDescriptor {
  readonly id: ConfigTab;
  readonly label: string;
  /** Becomes the screen's h1 while this tab is active. */
  readonly title: string;
  /**
   * May carry `{{ mailFrom }}`, the one placeholder the prototype puts in a subtitle.
   * Substituted by `resolveSubtitle`, never by the tab definition — these are module
   * constants and have no configuration in scope.
   */
  readonly subtitle: string;
  /** Any one of these grants the tab. */
  readonly permissions: readonly Permission[];
  readonly feature?: FeatureKey;
}

interface ConfigTabGroup {
  readonly label: string;
  readonly tabs: readonly ConfigTabDescriptor[];
}

/**
 * Fills the one placeholder a subtitle may carry.
 *
 * Blank stays visibly blank — «sin configurar» — instead of falling back to a
 * plausible address. This line is the portal's answer to «where does this mail come
 * from», and it spent its whole life answering with `vacaciones@knotion.com`, an
 * address that had never been created, while the notices went out signed by a
 * person. A wrong answer here sends someone to check a mailbox that does not exist.
 */
const resolveSubtitle = (subtitle: string, config: RuntimeConfig): string =>
  subtitle.replace(/\{\{\s*mailFrom\s*\}\}/g, () =>
    config.identity.mailFrom.length > 0 ? config.identity.mailFrom : 'sin configurar',
  );

const CONFIG_GROUPS: readonly ConfigTabGroup[] = [
  {
    label: 'Empresa',
    tabs: [
      {
        id: 'identidad',
        label: 'Identidad',
        title: 'Configuración global',
        subtitle:
          'Identidad, acceso y reglas base que heredan todas las áreas. Los cambios quedan en la bitácora de movimientos.',
        permissions: ['config'],
      },
      {
        id: 'areas',
        label: 'Áreas',
        title: 'Áreas de la empresa',
        subtitle: 'Cada área administra sus propias sub áreas.',
        permissions: ['areas'],
      },
      {
        id: 'feriados',
        label: 'Feriados',
        title: 'Feriados del país',
        subtitle:
          'México · descansos obligatorios de la LFT · 2026. Los feriados activos no se descuentan de tus días de vacaciones.',
        permissions: ['feriados_tab', 'editar_feriados'],
        feature: 'feriados',
      },
    ],
  },
  {
    label: 'Políticas',
    tabs: [
      {
        id: 'reglas',
        label: 'Reglas y saldos',
        title: 'Reglas y saldos',
        subtitle: 'Mínimos, anticipación, vencimiento y la tabla de días por antigüedad.',
        permissions: ['config'],
      },
      {
        id: 'catalogos',
        label: 'Catálogos',
        title: 'Catálogos',
        subtitle:
          'Tipos de incapacidad y permiso, y los motivos de rechazo que verá quien decida.',
        permissions: ['config'],
      },
      {
        id: 'roles',
        label: 'Roles y permisos',
        title: 'Roles y permisos',
        subtitle:
          'Qué puede hacer cada rol. Gobierna la interfaz; el piso de seguridad vive en las reglas de Firestore.',
        permissions: ['config'],
      },
    ],
  },
  {
    label: 'Sistema',
    tabs: [
      {
        id: 'correos',
        label: 'Correos',
        title: 'Notificaciones por correo',
        /*
         * `{{ mailFrom }}` is the prototype's own text, kept verbatim and substituted
         * at render. It was transcribed as the literal `vacaciones@knotion.com` — an
         * address nobody had created, printed as fact on the screen whose job is to
         * say where the portal's mail comes from. See `resolveSubtitle`.
         */
        subtitle:
          'Qué correo se envía en cada evento, a quién y con qué plantilla. Remitente: {{ mailFrom }}',
        permissions: ['config'],
      },
      {
        id: 'features',
        label: 'Features',
        title: 'Features y módulos',
        subtitle:
          'Activa o desactiva capacidades para todas las áreas y sub áreas. El cambio aplica al instante en cada portal.',
        permissions: ['features'],
      },
      /*
       * No viene del prototipo: es del asistente de WhatsApp, del 5 oct 2026.
       *
       * Sin `feature` a propósito. El módulo se enciende en la pestaña de al lado,
       * y esto —con qué modelo entiende y sus llaves— tiene que poder dejarse
       * listo **antes** de encenderlo: una pestaña que sólo apareciera con el
       * módulo ya encendido obligaría a abrir el asistente a medio configurar.
       */
      {
        id: 'asistente',
        label: 'Asistente',
        title: 'Asistente de WhatsApp',
        subtitle:
          'Con qué entiende lo que le escriben —palabras clave, OpenAI o Anthropic— y las llaves de cada uno. El cambio aplica desde el siguiente mensaje.',
        permissions: ['config'],
      },
    ],
  },
];

/**
 * Qué parte de la configuración se está viendo.
 *
 * Dos de las ocho pestañas del hub no son configuración global: **Áreas** es
 * Organization y **Correos** es Communications — es literalmente el catálogo de
 * qué aviso se manda en cada evento. Estaban ahí porque el hub existía antes que
 * el esquema de módulos, y su efecto era que dos estantes se vieran vacíos
 * teniendo su contenido a dos clics.
 *
 * Salen a sus módulos **sin cambiar de alcance**: las dos siguen siendo del
 * superusuario, con los mismos permisos que ya exigían. Lo que cambia es dónde se
 * encuentran, no quién puede abrirlas.
 *
 * Segunda divergencia deliberada del prototipo, que agrupa las ocho detrás de
 * «Configuración de la empresa». La autoriza la decisión 1 de `docs/KNPEOPLE.md`.
 */
export type ConfigScope = 'global' | 'areas' | 'correos';

/** Las pestañas que se quedan fuera del hub, ahora que tienen casa propia. */
const MODULE_TABS: readonly ConfigTab[] = ['areas', 'correos'];

export const ConfigScreen = ({ scope = 'global' }: { scope?: ConfigScope } = {}): ReactNode => {
  const { holds, featureOn } = useAccess();
  /* Only for `resolveSubtitle` — the Correos header names the configured sender. */
  const config = useRuntimeConfig();
  const areaSummary = useQuery({
    queryKey: ['system', 'configAreaSummary'],
    enabled: holds('areas'),
    queryFn: async () => {
      const result = await getServices().organization.listAreas();
      return result.ok ? result.value : [];
    },
  });

  /** Only the tabs this session may actually open, dentro de su alcance. */
  const groups = CONFIG_GROUPS.map((group) => ({
    label: group.label,
    tabs: group.tabs.filter(
      (t) =>
        (scope === 'global' ? !MODULE_TABS.includes(t.id) : t.id === scope) &&
        (t.feature === undefined || featureOn(t.feature)) &&
        t.permissions.some(holds),
    ),
  })).filter((group) => group.tabs.length > 0);

  const available = groups.flatMap((group) => group.tabs);
  const [tab, setTab] = useState<ConfigTab | null>(null);
  const [dirty, setDirty] = useState(false);
  /* Abierto desde la cabecera, dibujado dentro de `AreasPanel`, que es quien
     tiene el formulario y la mutación. */
  const [creatingArea, setCreatingArea] = useState(false);

  // The landing tab depends on the role — a superuser without `config` starts on
  // Features — so it is resolved from what survived filtering rather than pinned.
  const active = available.find((t) => t.id === tab) ?? available[0];

  if (active === undefined) {
    return (
      <Card title="Configuración de la empresa">
        <Banner tone="info">Tu perfil no tiene ninguna sección de configuración asignada.</Banner>
      </Card>
    );
  }

  return (
    <div className="flow">
      <header className={styles.header}>
        <div>
          <div className={styles.eyebrow}>Superusuario</div>
          {/*
            * Fuera del hub la cabecera es la de la pestaña.
            *
            * «Configuración global» encabezando la pantalla de Áreas dentro del
            * módulo Organización diría que el usuario está en otro sitio del que
            * está — y el rótulo de la barra lateral y el h1 no se pueden
            * contradecir.
            */}
          <PageTitle className={styles.title}>
            {scope === 'global' ? 'Configuración global' : active.title}
          </PageTitle>
          <p className={styles.note}>
            {scope === 'global'
              ? 'Identidad, acceso y reglas base que heredan todas las áreas. Los cambios quedan en la bitácora de movimientos.'
              : resolveSubtitle(active.subtitle, config)}
          </p>
        </div>
        <div className={styles.headActions}>
          {dirty && (
            <Chip background="var(--color-warning-bg)" foreground="var(--color-warning-ink)">
              Cambios sin guardar
            </Chip>
          )}
          {/*
            La acción propia de esta pestaña, al lado de la genérica. El estado
            vive aquí y no en `AreasPanel` porque el botón que lo enciende está en
            esta cabecera, que es del contenedor.
          */}
          {active.id === 'areas' && (
            /* `ghostButton` y no el `Button` del sistema de diseño: esta pantalla
               viene transcrita del prototipo y tiene su propio juego de botones.
               Comparte los 13 px de «Guardar cambios», que con el `Button` —14 px
               y otro alto— quedaban visiblemente distintos uno al lado del otro. */
            <button
              type="button"
              className={styles.ghostButton}
              onClick={() => {
                setCreatingArea(true);
              }}
            >
              Crear área
            </button>
          )}
          <button
            type="button"
            className={styles.configSave}
            onClick={() => {
              setDirty(false);
            }}
          >
            Guardar cambios
          </button>
        </div>
      </header>

      {/* Grouped tab tracks: a small uppercase label over each segmented pill
          row, exactly as `cfgGroups` renders them. */}
      {/*
        * Con una sola pestaña la pista no se dibuja: mismo criterio que en el
        * panel administrador partido — un control segmentado de un segmento es
        * un rótulo que parece pulsable.
        *
        * Se quita del árbol y no con el atributo `hidden`, que fue el primer
        * intento y no hizo nada: `hidden` es `display: none` de la hoja de
        * estilos del navegador, y cualquier regla propia con `display` lo gana.
        * `.tabGroups` declara `display: flex`, así que la pista seguía dibujada.
        */}
      {available.length > 1 && (
      <div className={styles.tabGroups}>
        {groups.map((group) => (
          <div key={group.label} className={styles.tabGroup}>
            <span className={styles.tabGroupLabel}>{group.label}</span>
            <div className={styles.tabTrack} role="tablist" aria-label={group.label}>
              {group.tabs.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  role="tab"
                  aria-selected={option.id === active.id}
                  className={cx(styles.tab, option.id === active.id && styles.tabActive)}
                  onClick={() => {
                    setTab(option.id);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      )}

      {scope === 'global' &&
        (active.id === 'areas' ||
          active.id === 'correos' ||
          active.id === 'features' ||
          active.id === 'asistente') && (
        <div
          className={cx(
            styles.configContext,
            (active.id === 'features' || active.id === 'asistente') && styles.configContextSmall,
          )}
        >
          {active.id === 'areas' && <div className={styles.eyebrow}>Superusuario</div>}
          <div className={styles.configContextLine}>
            <div>
              <h2 className={styles.configContextTitle}>{active.title}</h2>
              <p className={styles.note}>{resolveSubtitle(active.subtitle, config)}</p>
            </div>
            {active.id === 'areas' && (
              <Chip background="var(--color-violet-bg)" foreground="var(--color-violet-ink)">
                {(areaSummary.data ?? []).filter((area) => area.active).length} de{' '}
                {(areaSummary.data ?? []).length} activas
              </Chip>
            )}
          </div>
        </div>
      )}

      {active.id === 'identidad' && <IdentityTab />}
      {active.id === 'areas' && (
        <AreasPanel
          creating={creatingArea}
          onCloseCreate={() => {
            setCreatingArea(false);
          }}
        />
      )}
      {active.id === 'feriados' && <HolidaysPanel />}
      {active.id === 'reglas' && <RulesTab />}
      {active.id === 'roles' && <RolesTab />}
      {active.id === 'catalogos' && <CatalogsTab />}
      {active.id === 'features' && <FeaturesPanel />}
      {active.id === 'correos' && <MailsTab />}
      {active.id === 'asistente' && <AssistantSettingsTab />}
    </div>
  );
};

/**
 * Identidad y acceso.
 *
 * The company name and the allowed domain are not decoration: the domain is
 * what the blocking function reads to decide who gets a session at all, which
 * is why editing it warns rather than just saving.
 *
 * Every field writes on blur rather than on each keystroke — the runtime config
 * broadcasts to every open session, and a half-typed domain reaching them would
 * lock people out mid-edit.
 */
const IdentityTab = (): ReactNode => {
  const config = useRuntimeConfig();
  const toast = useToast();
  /*
   * Owned here rather than inside each field, and that is the whole point: a
   * field saves `{...identity, ...patch}`, so the domain field and the RR.HH.
   * address saving at once would have one of them written back stale. The two
   * access switches share it for the same reason — both write `featureFlags`.
   */
  const action = usePendingActions();
  const saving = action.busy;
  const { identity } = config;

  const saveIdentity = async (
    patch: Partial<typeof identity>,
    message: string,
  ): Promise<void> => {
    const result = await getServices().config.save({ identity: { ...identity, ...patch } });
    if (!result.ok) {
      toast.bad(`No se pudo guardar la identidad.${reasonFor(result.error)}`);
      return;
    }
    toast.ok(message);
  };

  const fields: readonly {
    key: string;
    label: string;
    note: string;
    value: string;
    apply: (next: string) => Promise<void>;
  }[] = [
    {
      key: 'nombre',
      label: 'Nombre comercial',
      note: 'Aparece en el encabezado y los correos',
      value: identity.commercialName,
      apply: (next) => saveIdentity({ commercialName: next }, 'Nombre comercial actualizado.'),
    },
    {
      key: 'dominio',
      label: 'Dominio corporativo permitido',
      note: 'Solo cuentas de Google con este dominio pueden ingresar',
      value: identity.allowedDomains.join(', '),
      apply: (next) =>
        saveIdentity(
          {
            allowedDomains: next
              .split(',')
              .map((d) => d.trim().replace(/^@/, ''))
              .filter((d) => d.length > 0),
          },
          'Dominio actualizado. Afecta quién puede iniciar sesión.',
        ),
    },
    {
      key: 'rrhh',
      label: 'Correo de RR.HH.',
      note: 'Recibe copia de cada solicitud aprobada',
      value: identity.hrEmail,
      apply: (next) => saveIdentity({ hrEmail: next.trim() }, 'Correo de RR.HH. actualizado.'),
    },
    {
      /*
       * Not derivable, which is why it is a field.
       *
       * The Trigger Email extension delivers through Gmail, and Gmail overrides the
       * extension's «Default FROM» with whichever account authenticated the SMTP. So
       * the portal cannot know its own sender: somebody has to write down what the
       * mail goes out as. Both screens that showed it used to build
       * `vacaciones@${domain}` and print an address that had never been created.
       */
      key: 'remitente',
      label: 'Remitente de los correos',
      note: 'La cuenta que autentica el SMTP. Vacío se muestra como «sin configurar»',
      value: identity.mailFrom,
      apply: (next) => saveIdentity({ mailFrom: next.trim() }, 'Remitente actualizado.'),
    },
    {
      key: 'pais',
      label: 'País de operación',
      note: 'Define el calendario de feriados aplicado',
      value: identity.country,
      apply: (next) => saveIdentity({ country: next.trim() }, 'País actualizado.'),
    },
  ];

  return (
    <div className={styles.identitySplit}>
      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>Identidad</h2>
        <p className={styles.panelNote}>
          Cómo se identifica la empresa en el portal y en los correos.
        </p>

        <div className={styles.logoRow}>
          <span className={styles.logoMark} aria-hidden="true">
            <img src={logoUrl} alt="" className={styles.logoImage} />
          </span>
          <span className={styles.logoText}>
            <span className={styles.logoTitle}>Logotipo corporativo</span>
            <span className={styles.logoNote}>PNG transparente · usado en portal y PDF</span>
          </span>
          <button
            type="button"
            className={styles.logoReplace}
            onClick={() => {
              toast.info('Sube el nuevo logotipo: PNG transparente, mínimo 240 px de alto.');
            }}
          >
            Reemplazar
          </button>
        </div>

        <div className={styles.identityFields}>
          {fields.map((field) => (
            <IdentityField
              key={field.key}
              label={field.label}
              note={field.note}
              value={field.value}
              saving={saving}
              apply={(next) => {
                action.run('identity', () => field.apply(next));
              }}
            />
          ))}
        </div>
      </section>

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>Acceso</h2>
        <p className={styles.panelNote}>
          Solo se permite el ingreso con Google del dominio corporativo.
        </p>

        {/*
          These four read from the feature flags they actually correspond to, so
          the switch here and the behaviour elsewhere cannot disagree. «Ingreso
          exclusivo con Google» has no flag because it is not optional — it is
          enforced by the blocking function and the security rules, and offering
          a switch would imply it could be turned off from the client.
        */}
        <ul className={styles.accessList}>
          <li className={styles.accessRow}>
            <span className={styles.accessText}>
              <span className={styles.accessLabel}>Ingreso exclusivo con Google</span>
              <span className={styles.accessNote}>
                Impuesto en el servidor; no se puede desactivar desde aquí.
              </span>
            </span>
            <Chip background="var(--color-success-bg)" foreground="var(--color-success-ink)">
              Siempre activo
            </Chip>
          </li>
          <AccessToggle
            feature="reemplazo"
            label="El reemplazo debe confirmar"
            note="La solicitud no avanza sin su aceptación"
            saving={saving}
            run={(fn) => {
              action.run('identity', fn);
            }}
          />
        </ul>
      </section>
    </div>
  );
};

/**
 * One identity field.
 *
 * Local while focused, committed on blur — see the tab's note on why a
 * keystroke-by-keystroke write is dangerous for the domain in particular.
 */
const IdentityField = ({
  label,
  note,
  value,
  saving,
  apply,
}: {
  label: string;
  note: string;
  value: string;
  /** Any field on the tab is saving. See the note where the hook is created. */
  saving: boolean;
  apply: (next: string) => void;
}): ReactNode => {
  const [text, setText] = useState(value);

  useEffect(() => {
    setText(value);
  }, [value]);

  return (
    <label className={styles.tierField}>
      <span className={styles.tierFieldLabel}>{label}</span>
      <input
        type="text"
        className={cx(styles.tierInput, saving && styles.pending)}
        value={text}
        disabled={saving}
        onChange={(event) => {
          setText(event.target.value);
        }}
        onBlur={() => {
          if (text.trim() === value.trim()) return;
          apply(text);
        }}
      />
      <span className={styles.accessNote}>{saving ? 'Guardando…' : note}</span>
    </label>
  );
};

/** An access rule backed by a feature flag. */
const AccessToggle = ({
  feature,
  label,
  note,
  saving,
  run,
}: {
  feature: FeatureKey;
  label: string;
  note: string;
  saving: boolean;
  run: (action: () => Promise<void>) => void;
}): ReactNode => {
  const config = useRuntimeConfig();
  const toast = useToast();
  const on = config.features[feature];

  return (
    <li className={styles.accessRow}>
      <span className={styles.accessText}>
        <span className={styles.accessLabel}>{label}</span>
        <span className={styles.accessNote}>{saving ? 'Guardando…' : note}</span>
      </span>
      <Toggle
        on={on}
        disabled={saving}
        label={`${on ? 'Desactivar' : 'Activar'} ${label}`}
        onToggle={() => {
          run(async () => {
            const result = await getServices().config.save({
              features: { ...config.features, [feature]: !on },
            });
            if (!result.ok) {
              toast.bad(`No se pudo guardar la regla de acceso.${reasonFor(result.error)}`);
              return;
            }
            toast.ok(`${label}: ${on ? 'desactivado' : 'activado'}.`);
          });
        }}
      />
    </li>
  );
};

/**
 * Reglas y saldos.
 *
 * Editable, not a read-out. These are the numbers that decide how many days
 * every person in the company gets, and the superuser is the only role that may
 * move them — so the panel is here rather than in the area administrator's
 * screen, which shows the same figures as context it cannot change.
 *
 * The seniority table ships as the LFT's own brackets. What the company grants
 * beyond the law is a separate «días adicionales» figure rather than inflated
 * bracket values, so the statutory floor stays legible next to the total.
 */
const RulesTab = (): ReactNode => {
  const config = useRuntimeConfig();
  const session = useSession();
  const toast = useToast();
  /*
   * One key for the panel, because `saveRules` writes the whole `config/rules`
   * document from the render that fired it — same lost-update hazard as the
   * catalogues, and here the casualty would be a vacation entitlement.
   *
   * Deliberately NOT wired to the `Stepper`s below. A stepper holds its figure
   * locally so twelve quick taps move the number by twelve — see its own note.
   * Refusing the second tap while the first write is in flight would leave the
   * shown value ahead of the stored one, which is worse than an extra request:
   * the screen would claim a rule the company is not operating under. They
   * already answer instantly, which is what this feedback is for; the controls
   * below did not.
   */
  const action = usePendingActions();
  const saving = action.busy;
  const { rules } = config;
  const { seniority } = rules;

  const [newYear, setNewYear] = useState('');
  const [newDays, setNewDays] = useState('');

  /** The viewer's own tenure, for the «Tu tramo» marker. */
  const me = useQuery({
    queryKey: ['rules', 'me', session.personId],
    queryFn: async () => {
      const result = await getServices().people.getById(session.personId);
      return result.ok ? result.value : null;
    },
  });

  const myYears =
    me.data === null || me.data === undefined || me.data.hireDate === null
      ? null
      : completedYearsOfService(me.data.hireDate, getServices().clock.today());

  const saveRules = async (patch: Partial<RulesConfig>, message: string): Promise<void> => {
    const result = await getServices().config.save({ rules: { ...rules, ...patch } });
    if (!result.ok) {
      toast.bad(`No se pudo guardar la regla.${reasonFor(result.error)}`);
      return;
    }
    toast.ok(message);
  };

  const saveSeniority = async (
    patch: Partial<SeniorityConfig>,
    message: string,
  ): Promise<void> => {
    await saveRules({ seniority: { ...seniority, ...patch } }, message);
  };

  /*
   * Each rule carries its own step and bounds.
   *
   * The notice period moves in fives because nobody sets it to 16, and the
   * expiry in threes because it is quarters — steppers that move by one there
   * make the control tedious rather than precise.
   */
  const vacationRules: readonly {
    key: string;
    label: string;
    note: string;
    value: number;
    suffix: string;
    step: number;
    min: number;
    max: number;
    apply: (next: number) => Promise<void>;
  }[] = [
    {
      key: 'base',
      label: 'Días por periodo',
      note: 'LFT mínimo 12 · empresa otorga más',
      value: seniority.fixedDays,
      suffix: 'días',
      step: 1,
      min: rules.legalMinimumDays,
      max: 60,
      apply: (next) => saveSeniority({ fixedDays: next }, `Días por periodo: ${String(next)}.`),
    },
    {
      key: 'aviso',
      label: 'Aviso previo',
      note: 'Días de anticipación para solicitar',
      value: rules.noticeDays,
      suffix: 'días',
      step: 5,
      min: 0,
      max: 60,
      apply: (next) => saveRules({ noticeDays: next }, `Aviso previo: ${String(next)} días.`),
    },
    {
      key: 'minimo',
      label: 'Mínimo por solicitud',
      note: 'Días hábiles por solicitud',
      value: rules.minimumRequestDays,
      suffix: 'día(s)',
      step: 1,
      min: 1,
      max: 10,
      apply: (next) =>
        saveRules({ minimumRequestDays: next }, `Mínimo por solicitud: ${String(next)}.`),
    },
    {
      key: 'vence',
      label: 'Vencimiento del saldo',
      note: 'Meses desde el aniversario para gozar los días de ese periodo',
      value: rules.expiryMonths,
      suffix: 'meses',
      step: 3,
      /* Menos de 12 dejaría días que vencen antes de que abra el periodo
         siguiente; la LFT y el servidor no bajan de ahí. */
      min: EXPIRY_MONTHS_MIN,
      max: EXPIRY_MONTHS_MAX,
      apply: (next) =>
        saveRules({ expiryMonths: next }, `Vencimiento del saldo: ${String(next)} meses.`),
    },
  ];

  const sorted = [...seniority.brackets].sort((a, b) => a.fromYear - b.fromYear);

  const typicalDays =
    myYears === null
      ? null
      : (sorted.filter((b) => myYears >= b.fromYear).at(-1)?.days ?? 0) + seniority.additionalDays;

  const addTier = async (): Promise<void> => {
    const year = Number.parseInt(newYear, 10);
    const days = Number.parseInt(newDays, 10);

    const result = addBracket(seniority, year, days);
    if (!result.ok) {
      toast.warn(
        result.error.kind === 'duplicate-year'
          ? `Ya existe un tramo que arranca en ${String(result.error.fromYear)} años.`
          : 'Faltan datos: indica desde cuántos años aplica y cuántos días otorga.',
      );
      return;
    }

    // Awaited rather than fired and forgotten, so the button can say it is
    // working — and so the two fields survive a failed save instead of being
    // cleared under someone who now has to retype them.
    await saveSeniority(
      { brackets: result.seniority.brackets },
      `Tramo agregado. Desde ${String(year)} años: ${String(days)} días de saldo.`,
    );
    setNewYear('');
    setNewDays('');
  };

  const dropTier = async (fromYear: number): Promise<void> => {
    // The table has to resolve to something for every tenure; emptying it would
    // silently drop everyone to the legal floor.
    if (sorted.length <= 1) {
      toast.warn('Debe quedar un tramo: la tabla necesita al menos uno.');
      return;
    }
    await saveSeniority(
      { brackets: removeBracket(seniority, fromYear).brackets },
      'Tramo eliminado. El saldo se toma del tramo anterior.',
    );
  };

  return (
    <>
      <section className={styles.panel}>
        <div className={styles.panelHead}>
          <div className={styles.panelHeadText}>
            <h2 className={styles.panelTitle}>Reglas de vacaciones</h2>
            <p className={styles.panelNote}>Aplican como mínimo sobre lo que marca la LFT.</p>
          </div>
        </div>

        <ul className={styles.ruleRows}>
          {vacationRules.map((rule) => (
            <li key={rule.key} className={styles.ruleRow}>
              <span className={styles.ruleRowText}>
                <span className={styles.ruleRowLabel}>{rule.label}</span>
                <span className={styles.ruleRowNote}>{rule.note}</span>
              </span>
              <Stepper
                value={rule.value}
                suffix={rule.suffix}
                label={rule.label}
                onChange={(next) => {
                  void rule.apply(Math.max(rule.min, Math.min(rule.max, next)));
                }}
                step={rule.step}
                min={rule.min}
                max={rule.max}
              />
            </li>
          ))}
        </ul>
      </section>

      <section className={styles.panel}>
        <div className={styles.panelHead}>
          <div className={styles.panelHeadText}>
            <h2 className={styles.panelTitle}>Días de saldo por antigüedad</h2>
            <p className={styles.panelNote}>
              {seniority.enabled
                ? 'El saldo de cada persona sale del tramo que le toca por años cumplidos, más los días adicionales de la empresa.'
                : 'Con la tabla apagada, todos reciben los días fijos configurados arriba.'}
            </p>
          </div>
          <div className={styles.tenureHead}>
            <Chip background="var(--color-brand-bg-alt)" foreground="var(--color-brand-ink)">
              {sorted.length} {sorted.length === 1 ? 'tramo' : 'tramos'} ·{' '}
              {seniority.enabled
                ? typicalDays === null
                  ? 'tabla activa'
                  : `saldo tipo ${String(typicalDays)} días`
                : 'tabla apagada'}
            </Chip>
            <span className={styles.tenureToggleLabel}>
              {seniority.enabled ? 'Tabla activa' : 'Tabla apagada'}
            </span>
            <Toggle
              on={seniority.enabled}
              disabled={saving}
              label={`${seniority.enabled ? 'Apagar' : 'Encender'} la tabla por antigüedad`}
              onToggle={() => {
                action.run('rules', () =>
                  saveSeniority(
                    { enabled: !seniority.enabled },
                    seniority.enabled
                      ? `Tabla apagada. Todos reciben ${String(seniority.fixedDays)} días fijos.`
                      : 'Tabla activa. El saldo sale del tramo por años cumplidos.',
                  ),
                );
              }}
            />
          </div>
        </div>

        <div className={styles.tierGrid}>
          {sorted.map((bracket, index) => {
            const next = sorted[index + 1];
            const label =
              next === undefined
                ? `${String(bracket.fromYear)} años o más`
                : next.fromYear - 1 === bracket.fromYear
                  ? `${String(bracket.fromYear)}° año`
                  : `${String(bracket.fromYear)} a ${String(next.fromYear - 1)} años`;
            const mine =
              myYears !== null &&
              myYears >= bracket.fromYear &&
              (next === undefined || myYears < next.fromYear);

            return (
              <div
                key={bracket.fromYear}
                className={cx(styles.tierCard, mine && styles.tierCardMine)}
              >
                <div className={styles.tierHead}>
                  <span className={styles.tierText}>
                    <span className={styles.tierRange}>{label}</span>
                    <span className={styles.tierTotal}>
                      Saldo total {bracket.days + seniority.additionalDays} días con los
                      adicionales
                    </span>
                  </span>
                  {mine && (
                    <Chip background="var(--color-brand-bg)" foreground="var(--color-brand-ink)">
                      Tu tramo
                    </Chip>
                  )}
                </div>

                <div className={styles.tierControls}>
                  <Stepper
                    value={bracket.days}
                    suffix="días"
                    label={`Días del tramo ${label}`}
                    step={1}
                    min={1}
                    max={90}
                    onChange={(days) => {
                      void saveSeniority(
                        {
                          brackets: sorted.map((b) =>
                            b.fromYear === bracket.fromYear ? { ...b, days } : b,
                          ),
                        },
                        `${label}: ${String(days)} días.`,
                      );
                    }}
                  />
                  <button
                    type="button"
                    className={cx(styles.tierRemove, saving && styles.pending)}
                    disabled={saving}
                    onClick={() => {
                      action.run('rules', () => dropTier(bracket.fromYear));
                    }}
                  >
                    {saving ? 'Quitando…' : 'Quitar'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className={styles.tierFooter}>
          <label className={styles.tierField}>
            <span className={styles.tierFieldLabel}>Desde (años)</span>
            <input
              type="number"
              min={1}
              className={styles.tierInput}
              placeholder="21"
              value={newYear}
              onChange={(event) => {
                setNewYear(event.target.value);
              }}
            />
          </label>
          <label className={styles.tierField}>
            <span className={styles.tierFieldLabel}>Días de saldo</span>
            <input
              type="number"
              min={1}
              className={styles.tierInput}
              placeholder="28"
              value={newDays}
              onChange={(event) => {
                setNewDays(event.target.value);
              }}
            />
          </label>
          <button
            type="button"
            className={cx(styles.tierAdd, saving && styles.pending)}
            disabled={saving}
            onClick={() => {
              action.run('rules', addTier);
            }}
          >
            {saving ? 'Agregando…' : 'Agregar tramo'}
          </button>

          <div className={styles.extraCell}>
            <span className={styles.tierText}>
              <span className={styles.tierRange}>Días adicionales de empresa</span>
              <span className={styles.tierTotal}>Se suman a todos los tramos</span>
            </span>
            <Stepper
              value={seniority.additionalDays}
              suffix="días"
              label="Días adicionales de empresa"
              step={1}
              min={0}
              max={60}
              onChange={(days) => {
                void saveSeniority(
                  { additionalDays: days },
                  `Días adicionales de empresa: ${String(days)}.`,
                );
              }}
            />
          </div>
        </div>
      </section>
    </>
  );
};

/**
 * A − value + control.
 *
 * A stepper rather than a text field because every value here is bounded and
 * moves in known increments; typing invites a number the rule cannot hold and
 * then has to reject.
 *
 * It holds the figure locally rather than reading the prop on every click. The
 * write goes through the runtime configuration and comes back asynchronously,
 * so several quick clicks would each read the same stale value and collapse
 * into a single step — twelve taps moved the number by one. Local state makes
 * them accumulate and shows the change immediately; the effect adopts the prop
 * when it settles, or when another session edits the same rule over the live
 * listener.
 */
const Stepper = ({
  value,
  suffix,
  label,
  step,
  min,
  max,
  onChange,
}: {
  value: number;
  suffix: string;
  label: string;
  step: number;
  min: number;
  max: number;
  onChange: (next: number) => void;
}): ReactNode => {
  const [shown, setShown] = useState(value);

  useEffect(() => {
    setShown(value);
  }, [value]);

  const move = (delta: number): void => {
    setShown((current) => {
      const next = Math.max(min, Math.min(max, current + delta));
      if (next !== current) onChange(next);
      return next;
    });
  };

  return (
    <div className={styles.stepper}>
      <button
        type="button"
        className={styles.stepperButton}
        aria-label={`Menos — ${label}`}
        disabled={shown <= min}
        onClick={() => {
          move(-step);
        }}
      >
        −
      </button>
      <span className={styles.stepperValue}>
        {shown} {suffix}
      </span>
      <button
        type="button"
        className={styles.stepperButton}
        aria-label={`Más — ${label}`}
        disabled={shown >= max}
        onClick={() => {
          move(step);
        }}
      >
        +
      </button>
    </div>
  );
};

/**
 * The permission matrix.
 *
 * The protected rule is enforced here as well as in the callable: the superuser
 * cannot lose `features`, because that is the only route back to this screen and an
 * account that stripped it would lock itself out with no in-app recovery.
 */
const RolesTab = (): ReactNode => {
  const config = useRuntimeConfig();
  const toast = useToast();
  /*
   * One key, not one per switch: a toggle writes all four roles' arrays at once,
   * so two switches in flight would have the second restore the first's stale
   * copy — the classic way a permission change "does not stick".
   */
  const action = usePendingActions();
  const saving = action.busy;
  const [role, setRole] = useState<Role>('empleado');

  const matrix = config.rolePermissions;

  const toggle = async (permission: Permission): Promise<void> => {
    const result = togglePermission(matrix, role, permission);

    if (!result.ok) {
      toast.warn(
        'El superusuario no puede perder «Features y módulos»: quedaría sin acceso a la pantalla que lo otorga.',
      );
      return;
    }

    const saved = await getServices().config.save({ rolePermissions: result.matrix });
    if (!saved.ok) {
      toast.bad(`No se pudo guardar el permiso.${reasonFor(saved.error)}`);
      return;
    }
    toast.ok('Permisos actualizados para toda la empresa.');
  };

  const restore = async (): Promise<void> => {
    const saved = await getServices().config.save({ rolePermissions: resetRole(matrix, role) });
    if (!saved.ok) {
      toast.bad(`No se pudo restaurar.${reasonFor(saved.error)}`);
      return;
    }
    toast.ok(`${ROLE_LABEL[role]} volvió a sus permisos originales.`);
  };

  const held = matrix[role] ?? [];

  return (
    <Card
      title="Roles y permisos"
      note="Lo que puede ver y hacer un colaborador. El cambio aplica al instante en su navegación."
      action={
        <div className={styles.roleCardActions}>
          <Chip background="var(--color-violet-bg)" foreground="var(--color-violet-ink)">
            {held.length} de {PERMISSION_CATALOG.length} permisos
          </Chip>
          <Button
            small
            variant="secondary"
            busy={saving}
            onClick={() => {
              action.run('matrix', restore);
            }}
          >
            Restaurar original
          </Button>
        </div>
      }
    >
      <div className={shared.stack}>
        <div className={cx(styles.roleTabs, 'scroll-x')} role="tablist" aria-label="Rol">
          {ROLES.map((option) => (
            <button
              key={option}
              type="button"
              role="tab"
              aria-selected={role === option}
              className={cx(styles.roleTab, role === option && styles.roleTabActive)}
              onClick={() => {
                setRole(option);
              }}
            >
              {ROLE_LABEL[option]}
              <span className={styles.tabCount}>
                {(matrix[option] ?? []).length}/{PERMISSION_CATALOG.length}
              </span>
            </button>
          ))}
        </div>

        <div className={styles.groups}>
          {PERMISSION_GROUPS.map((group) => (
            <div key={group}>
              <div className={styles.groupHead}>
                <span className={styles.groupLabel}>{group}</span>
                <span className={styles.groupRule} aria-hidden="true" />
              </div>

              <ul className={styles.rowsTight}>
                {PERMISSION_CATALOG.filter((p) => p.group === group).map((permission) => {
                  const locked = isProtected(role, permission.key);
                  const on = held.includes(permission.key);


                  return (
                    <li
                      key={permission.key}
                      className={cx(styles.switchRow, !on && styles.switchRowOff)}
                    >
                      <span className={styles.rowText}>
                        <span className={styles.rowTitleLine}>
                          <span className={styles.rowTitle}>{permission.label}</span>
                          {locked && (
                            <Chip
                              background="var(--color-hairline)"
                              foreground="var(--color-text-faint)"
                            >
                              Protegido
                            </Chip>
                          )}
                        </span>
                        <span className={styles.rowNote}>{permission.note}</span>
                      </span>

                      <Toggle
                        on={on}
                        disabled={locked || saving}
                        label={`${on ? 'Quitar' : 'Otorgar'} ${permission.label} a ${ROLE_LABEL[role]}`}
                        onToggle={() => {
                          action.run('matrix', () => toggle(permission.key));
                        }}
                      />
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};

/**
 * Catálogos.
 *
 * Every row is a switch, not a read-only chip: turning a kind off has to hide it
 * from the wizards **without** deleting it, because history already references
 * it by name. That distinction is the whole reason these are `enabled` flags
 * rather than rows you remove.
 */
const CatalogsTab = (): ReactNode => {
  const config = useRuntimeConfig();
  const toast = useToast();
  /*
   * One key for the whole tab, not one per row — and that is a correctness
   * choice rather than a simplification.
   *
   * `saveCatalogs` sends `{...catalogs, ...patch}`: every list in the document,
   * read from the render that fired it. Two rows saving at once therefore have
   * the second write the first one's stale copy back, and the edit that
   * disappears is the earlier one — silently, with both toasts reporting
   * success. Serialising them is what makes that impossible; the visible cost is
   * that the tab's controls stand down together for a moment.
   *
   * Panels whose rows are separate documents — areas, holidays — key by row
   * instead, because there is nothing to clobber.
   */
  const action = usePendingActions();
  const saving = action.busy;
  const [scope, setScope] = useState<RejectionScope | 'todos'>('vacaciones');
  /*
   * Cuál de los dos catálogos se está editando.
   *
   * Estaban lado a lado y no cabían: a media pantalla cada fila de permiso parte
   * sus banderas en dos y tres líneas —medido: 153 y 197 px de alto, contra 109 de
   * una de incapacidad—, y las dos columnas acaban con 437 px de diferencia entre
   * sus fondos. A todo el ancho las filas caen a 70 px casi todas. Se ve uno a la
   * vez porque **no se editan juntos**: quien viene a apagar un tipo de permiso no
   * está mirando las incapacidades.
   */
  const [catalog, setCatalog] = useState<'incapacidades' | 'permisos'>('incapacidades');
  const { catalogs } = config;
  const rejectionMeta: Record<RejectionScope, { label: string; color: string }> = {
    vacaciones: { label: 'Vacaciones', color: 'var(--color-brand)' },
    permiso: { label: 'Permisos', color: 'var(--color-success)' },
    incapacidad: { label: 'Incapacidades', color: 'var(--color-warning)' },
    cancelacion: { label: 'Cancelación', color: 'var(--color-danger)' },
  };

  /*
   * Drafts for the three add forms.
   *
   * Held locally and only written to the runtime configuration on submit —
   * every keystroke going through `config.save` would broadcast a half-typed
   * catalogue to every open session over the OTA listener.
   */
  const [newIncapacity, setNewIncapacity] = useState({
    name: '',
    note: '',
    subsidy: 'Subsidio 60%',
  });
  const [newPermission, setNewPermission] = useState({
    name: '',
    note: '',
    byHours: false,
    /* Sólo significa algo con `byHours`; el formulario esconde el campo si no. */
    openEnded: false,
    withPay: true,
  });
  const [newReason, setNewReason] = useState('');
  const [reasonScope, setReasonScope] = useState<RejectionScope>('vacaciones');

  const incapacityUsage = useQuery({
    queryKey: ['system', 'catalog', 'incapacity-usage'],
    queryFn: async () => {
      const result = await getServices().incapacities.list({}, { limit: 100 });
      return result.ok ? result.value.items : [];
    },
  });

  const permissionUsage = useQuery({
    queryKey: ['system', 'catalog', 'permission-usage'],
    queryFn: async () => {
      const result = await getServices().permissions.list({}, { limit: 100 });
      return result.ok ? result.value.items : [];
    },
  });

  const incapacityCount = (kind: string): string => {
    const count = (incapacityUsage.data ?? []).filter((item) => item.kind === kind).length;
    return `${count} ${count === 1 ? 'caso' : 'casos'}`;
  };

  const permissionCount = (kind: string): string => {
    const count = (permissionUsage.data ?? []).filter((item) => item.kind === kind).length;
    return `${count} ${count === 1 ? 'solicitud' : 'solicitudes'}`;
  };

  const saveCatalogs = async (patch: Partial<typeof catalogs>, message: string): Promise<void> => {
    const result = await getServices().config.save({ catalogs: { ...catalogs, ...patch } });
    if (!result.ok) {
      toast.bad(`No se pudo guardar el catálogo.${reasonFor(result.error)}`);
      return;
    }
    toast.ok(message);
  };

  const toggleIncapacity = async (id: string, next: boolean): Promise<void> => {
    await saveCatalogs(
      {
        incapacityKinds: catalogs.incapacityKinds.map((k) =>
          k.id === id ? { ...k, enabled: next } : k,
        ),
      },
      `${id} ${next ? 'disponible' : 'oculto'} al registrar una incapacidad.`,
    );
  };

  const togglePermissionKind = async (id: string, next: boolean): Promise<void> => {
    await saveCatalogs(
      {
        permissionKinds: catalogs.permissionKinds.map((k) =>
          k.id === id ? { ...k, enabled: next } : k,
        ),
      },
      `${id} ${next ? 'disponible' : 'oculto'} al pedir un permiso.`,
    );
  };

  /*
   * Si el permiso vuelve ese día o no.
   *
   * Un interruptor del catálogo y no un nombre reconocido en el código: RR.HH.
   * puede dar de alta «Salida médica» y marcarla sin regreso sin que nadie
   * despliegue. Llega apagado en cualquier catálogo ya sembrado —el campo no
   * existía—, así que «Salir temprano» hay que encenderlo una vez aquí.
   */
  const toggleOpenEnded = async (id: string, next: boolean): Promise<void> => {
    await saveCatalogs(
      {
        permissionKinds: catalogs.permissionKinds.map((k) =>
          k.id === id ? { ...k, openEnded: next } : k,
        ),
      },
      next
        ? `${id} ya no pide hora de regreso.`
        : `${id} vuelve a pedir hora de regreso.`,
    );
  };

  /* Si el tipo se puede pedir para días ya pasados. Ver `PermissionKind.retroactive`. */
  const toggleRetroactive = async (id: string, next: boolean): Promise<void> => {
    await saveCatalogs(
      {
        permissionKinds: catalogs.permissionKinds.map((k) =>
          k.id === id ? { ...k, retroactive: next } : k,
        ),
      },
      next
        ? `${id} ya se puede registrar para días pasados.`
        : `${id} vuelve a pedirse sólo por adelantado.`,
    );
  };

  const toggleReason = async (id: string, next: boolean): Promise<void> => {
    await saveCatalogs(
      {
        rejectionReasons: catalogs.rejectionReasons.map((r) =>
          r.id === id ? { ...r, enabled: next } : r,
        ),
      },
      next ? 'Motivo disponible de nuevo.' : 'Motivo retirado de la lista.',
    );
  };

  /** A catalogue id is its display name, so a duplicate would be ambiguous. */
  const alreadyNamed = (names: readonly string[], candidate: string): boolean =>
    names.some((n) => n.toLowerCase() === candidate.toLowerCase());

  const addIncapacityKind = async (): Promise<void> => {
    const name = newIncapacity.name.trim();
    if (name.length < 3) {
      toast.warn('Falta el nombre: escribe cómo se llama el tipo de incapacidad.');
      return;
    }
    if (alreadyNamed(catalogs.incapacityKinds.map((k) => k.id), name)) {
      toast.warn(`${name} ya está en el catálogo.`);
      return;
    }
    await saveCatalogs(
      {
        incapacityKinds: [
          ...catalogs.incapacityKinds,
          {
            id: name,
            note: newIncapacity.note.trim() || 'Regla por definir con RR.HH.',
            subsidy: newIncapacity.subsidy,
            /* Un tipo nuevo lo certifica el IMSS; lo contrario es la excepción y
               hoy sólo la paternidad. */
            imssCertified: true,
            enabled: true,
          },
        ],
      },
      `${name} agregado. Ya aparece al registrar una incapacidad.`,
    );
    setNewIncapacity({ name: '', note: '', subsidy: 'Subsidio 60%' });
  };

  const addPermissionKind = async (): Promise<void> => {
    const name = newPermission.name.trim();
    if (name.length < 3) {
      toast.warn('Falta el nombre: escribe cómo se llama el tipo de permiso.');
      return;
    }
    if (alreadyNamed(catalogs.permissionKinds.map((k) => k.id), name)) {
      toast.warn(`${name} ya está en el catálogo.`);
      return;
    }
    await saveCatalogs(
      {
        permissionKinds: [
          ...catalogs.permissionKinds,
          {
            id: name,
            note: newPermission.note.trim() || 'Regla por definir con RR.HH.',
            // A neutral glyph: the design ships no icon picker, and a new kind
            // still has to render somewhere in the wizard.
            icon: '✦',
            measuredInHours: newPermission.byHours,
            /* Un permiso de días no tiene hora de vuelta que omitir, así que la
               elección se descarta si la unidad no es horas — el campo estaba
               oculto y su valor sería el residuo de haber cambiado de unidad. */
            openEnded: newPermission.byHours && newPermission.openEnded,
            /* Un tipo nuevo se pide por adelantado; lo retroactivo es la
               excepción y se marca en la fila. */
            retroactive: false,
            withPay: newPermission.withPay,
            enabled: true,
          },
        ],
      },
      `${name} agregado. Ya aparece al pedir un permiso.`,
    );
    setNewPermission({ name: '', note: '', byHours: false, openEnded: false, withPay: true });
  };

  const addReason = async (): Promise<void> => {
    const text = newReason.trim();
    // The same floor the leader is held to when they write one by hand; a
    // suggestion shorter than that would not explain the rejection either.
    if (text.length < 15) {
      toast.warn('Motivo muy corto: escribe al menos 15 caracteres, igual que se le pide al líder.');
      return;
    }
    if (
      catalogs.rejectionReasons.some(
        (r) => r.scope === reasonScope && r.text.toLowerCase() === text.toLowerCase(),
      )
    ) {
      toast.warn('Ese motivo ya está en la lista.');
      return;
    }
    await saveCatalogs(
      {
        rejectionReasons: [
          ...catalogs.rejectionReasons,
          {
            id: `${reasonScope}-${String(catalogs.rejectionReasons.length + 1)}`,
            text,
            scope: reasonScope,
            enabled: true,
          },
        ],
      },
      'Motivo agregado. Aparece como sugerencia al rechazar.',
    );
    setNewReason('');
  };

  const scopes: readonly (RejectionScope | 'todos')[] = [
    'todos',
    'vacaciones',
    'permiso',
    'incapacidad',
    'cancelacion',
  ];

  const reasons = catalogs.rejectionReasons.filter(
    (r) => scope === 'todos' || r.scope === scope,
  );

  return (
    <>
      <section className={styles.panel}>
        <div className={styles.catalogHead}>
          <div
            className={styles.catalogScopeTabs}
            role="tablist"
            aria-label="Catálogo que se está editando"
          >
            {(['incapacidades', 'permisos'] as const).map((option) => (
              <button
                key={option}
                type="button"
                role="tab"
                aria-selected={catalog === option}
                className={cx(
                  styles.catalogScopeTab,
                  catalog === option && styles.catalogScopeTabActive,
                )}
                onClick={() => {
                  setCatalog(option);
                }}
              >
                {option === 'incapacidades' ? 'Tipos de incapacidad' : 'Tipos de permiso'}
              </button>
            ))}
          </div>
          {catalog === 'incapacidades' ? (
            <Chip background="var(--color-warning-bg)" foreground="var(--color-warning-ink)">
              {catalogs.incapacityKinds.filter((k) => k.enabled).length} de{' '}
              {catalogs.incapacityKinds.length} activos
            </Chip>
          ) : (
            <Chip background="var(--color-success-bg)" foreground="var(--color-success-ink)">
              {catalogs.permissionKinds.filter((k) => k.enabled).length} de{' '}
              {catalogs.permissionKinds.length} activos
            </Chip>
          )}
        </div>

        {catalog === 'incapacidades' && (
          <>
          <p className={styles.panelNote}>
            Lo que aparece al registrar una incapacidad. Apágalo para ocultarlo sin borrar el
            historial.
          </p>

          <ul className={styles.rowsTight}>
            {catalogs.incapacityKinds.map((kind) => (
              <li
                key={kind.id}
                className={cx(styles.switchRow, !kind.enabled && styles.switchRowOff)}
              >
                <span className={styles.rowText}>
                  <span className={styles.rowTitle}>{kind.id}</span>
                  <span className={styles.rowNote}>{kind.note} · {incapacityCount(kind.id)}</span>
                </span>
                <span className={styles.rowControls}>
                  <Chip
                    background={kind.subsidy === 'Subsidio 60%' ? 'var(--color-warning-bg)' : 'var(--color-violet-bg)'}
                    foreground={kind.subsidy === 'Subsidio 60%' ? 'var(--color-warning-ink)' : 'var(--color-violet-ink)'}
                  >
                    {kind.subsidy}
                  </Chip>
                  <Toggle
                    on={kind.enabled}
                    color="var(--color-warning)"
                    disabled={saving}
                    label={`${kind.enabled ? 'Ocultar' : 'Mostrar'} ${kind.id}`}
                    onToggle={() => {
                      action.run('catalogs', () => toggleIncapacity(kind.id, !kind.enabled));
                    }}
                  />
                  <button
                    type="button"
                    className={cx(styles.removeLink, saving && styles.pending)}
                    disabled={saving}
                    onClick={() => {
                      action.run('catalogs', () =>
                        saveCatalogs(
                          {
                            incapacityKinds: catalogs.incapacityKinds.filter(
                              (item) => item.id !== kind.id,
                            ),
                          },
                          `${kind.id} eliminado.`,
                        ),
                      );
                    }}
                  >
                    Eliminar
                  </button>
                </span>
              </li>
            ))}
          </ul>

          <div className={styles.addForm}>
            <label className={styles.addField}>
              <span className={styles.addLabel}>Nuevo tipo</span>
              <input
                type="text"
                className={styles.addInput}
                placeholder="Ej. Enfermedad crónica"
                value={newIncapacity.name}
                onChange={(e) => {
                  setNewIncapacity((d) => ({ ...d, name: e.target.value }));
                }}
              />
            </label>
            <label className={styles.addField}>
              <span className={styles.addLabel}>Regla que verá el colaborador</span>
              <input
                type="text"
                className={styles.addInput}
                placeholder="Ej. Subsidio desde el 4º día"
                value={newIncapacity.note}
                onChange={(e) => {
                  setNewIncapacity((d) => ({ ...d, note: e.target.value }));
                }}
              />
            </label>
            <div className={styles.addChoices} role="group" aria-label="Subsidio">
              {['Subsidio 100%', 'Subsidio 60%', 'Sin subsidio'].map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={newIncapacity.subsidy === option}
                  className={cx(
                    styles.addChoice,
                    newIncapacity.subsidy === option && styles.addChoiceOnWarn,
                  )}
                  onClick={() => {
                    setNewIncapacity((d) => ({ ...d, subsidy: option }));
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
            <button
              type="button"
              className={cx(styles.addSubmit, styles.addSubmitWarn, saving && styles.pending)}
              disabled={saving}
              onClick={() => {
                action.run('catalogs', addIncapacityKind);
              }}
            >
              Agregar tipo
            </button>
          </div>
          </>
        )}

        {catalog === 'permisos' && (
          <>
          <p className={styles.panelNote}>
            Cada tipo define si se pide por días u horas y si es con goce de sueldo.
          </p>

          <ul className={styles.rowsTight}>
            {catalogs.permissionKinds.map((kind) => (
              <li
                key={kind.id}
                className={cx(styles.switchRow, !kind.enabled && styles.switchRowOff)}
              >
                <span
                  className={styles.kindGlyph}
                  style={cssVars({ '--kind-color': 'var(--color-success)' })}
                  aria-hidden="true"
                >
                  {kind.icon}
                </span>
                <span className={styles.rowText}>
                  <span className={styles.rowTitle}>{kind.id}</span>
                  <span className={styles.rowNote}>{kind.note} · {permissionCount(kind.id)}</span>
                </span>
                <span className={styles.rowControls}>
                  <Chip
                    background="var(--color-surface-subtle-alt)"
                    foreground="var(--color-text-tertiary-alt)"
                  >
                    {kind.measuredInHours ? 'Por horas' : 'Por días'}
                  </Chip>
                  <Chip
                    background={
                      kind.withPay ? 'var(--color-success-bg)' : 'var(--color-warning-bg)'
                    }
                    foreground={
                      kind.withPay ? 'var(--color-success-ink)' : 'var(--color-warning-ink)'
                    }
                  >
                    {kind.withPay ? 'Con goce' : 'Sin goce'}
                  </Chip>
                  <Toggle
                    on={kind.enabled}
                    color="var(--color-success)"
                    label={`${kind.enabled ? 'Ocultar' : 'Mostrar'} ${kind.id}`}
                    onToggle={() => {
                      action.run('catalogs', () =>
                        togglePermissionKind(kind.id, !kind.enabled),
                      );
                    }}
                  />
                  {/*
                    Si regresa ese día, con las dos opciones a la vista.

                    Era un enlace que decía «Marcar sin regreso» y no se leía como
                    un campo: no enseñaba el estado actual, sólo la acción de
                    cambiarlo, y quien no supiera que existe la distinción no tenía
                    por qué mirarlo. Un par segmentado dice en qué está y qué es lo
                    otro, igual que «Días / Horas» justo debajo.

                    Sólo en un permiso de horas: un día completo no tiene hora de
                    vuelta que omitir.
                  */}
                  {kind.measuredInHours && (
                    <span
                      className={styles.addChoices}
                      role="group"
                      aria-label={`${kind.id}: si regresa ese día`}
                    >
                      {[
                        { label: 'Regresa', value: false },
                        { label: 'No regresa', value: true },
                      ].map((option) => (
                        <button
                          key={option.label}
                          type="button"
                          disabled={saving}
                          aria-pressed={kind.openEnded === option.value}
                          title={
                            option.value
                              ? 'Se va y no vuelve ese día: el formulario deja de pedir la hora de regreso.'
                              : 'Sale y vuelve el mismo día: el formulario pide las dos horas.'
                          }
                          className={cx(
                            styles.addChoice,
                            kind.openEnded === option.value && styles.addChoiceOn,
                            saving && styles.pending,
                          )}
                          onClick={() => {
                            if (kind.openEnded === option.value) return;
                            action.run('catalogs', () =>
                              toggleOpenEnded(kind.id, option.value),
                            );
                          }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </span>
                  )}
                  {/* Cuándo se puede pedir. Paternidad y fallecimiento se
                      registran después de que pasó lo que los motiva; el resto va
                      de hoy en adelante. */}
                  <span
                    className={styles.addChoices}
                    role="group"
                    aria-label={`${kind.id}: cuándo se puede pedir`}
                  >
                    {[
                      { label: 'Desde hoy', value: false },
                      { label: 'También pasado', value: true },
                    ].map((option) => (
                      <button
                        key={option.label}
                        type="button"
                        disabled={saving}
                        aria-pressed={kind.retroactive === option.value}
                        title={
                          option.value
                            ? 'Se puede registrar para días que ya pasaron: sirve para lo que se pide después de ocurrir.'
                            : 'De hoy en adelante. El servidor rechaza los días que ya pasaron.'
                        }
                        className={cx(
                          styles.addChoice,
                          kind.retroactive === option.value && styles.addChoiceOn,
                          saving && styles.pending,
                        )}
                        onClick={() => {
                          if (kind.retroactive === option.value) return;
                          action.run('catalogs', () =>
                            toggleRetroactive(kind.id, option.value),
                          );
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </span>
                  <button
                    type="button"
                    className={cx(styles.removeLink, saving && styles.pending)}
                    disabled={saving}
                    onClick={() => {
                      action.run('catalogs', () =>
                        saveCatalogs(
                          {
                            permissionKinds: catalogs.permissionKinds.filter(
                              (item) => item.id !== kind.id,
                            ),
                          },
                          `${kind.id} eliminado.`,
                        ),
                      );
                    }}
                  >
                    Eliminar
                  </button>
                </span>
              </li>
            ))}
          </ul>

          <div className={styles.addForm}>
            <label className={styles.addField}>
              <span className={styles.addLabel}>Nuevo tipo</span>
              <input
                type="text"
                className={styles.addInput}
                placeholder="Ej. Permiso de estudios"
                value={newPermission.name}
                onChange={(e) => {
                  setNewPermission((d) => ({ ...d, name: e.target.value }));
                }}
              />
            </label>
            <label className={styles.addField}>
              <span className={styles.addLabel}>Regla que verá el colaborador</span>
              <input
                type="text"
                className={styles.addInput}
                placeholder="Ej. Hasta 3 días por semestre"
                value={newPermission.note}
                onChange={(e) => {
                  setNewPermission((d) => ({ ...d, note: e.target.value }));
                }}
              />
            </label>
            <div className={styles.addChoices} role="group" aria-label="Unidad">
              {[
                { label: 'Días', value: false },
                { label: 'Horas', value: true },
              ].map((option) => (
                <button
                  key={option.label}
                  type="button"
                  aria-pressed={newPermission.byHours === option.value}
                  className={cx(
                    styles.addChoice,
                    newPermission.byHours === option.value && styles.addChoiceOn,
                  )}
                  onClick={() => {
                    setNewPermission((d) => ({ ...d, byHours: option.value }));
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {/* Aparece con la unidad en horas y desaparece con ella: en días no
                hay hora de vuelta que omitir. */}
            {newPermission.byHours && (
              <div className={styles.addChoices} role="group" aria-label="Si regresa ese día">
                {[
                  { label: 'Regresa', value: false },
                  { label: 'No regresa', value: true },
                ].map((option) => (
                  <button
                    key={option.label}
                    type="button"
                    aria-pressed={newPermission.openEnded === option.value}
                    className={cx(
                      styles.addChoice,
                      newPermission.openEnded === option.value && styles.addChoiceOn,
                    )}
                    onClick={() => {
                      setNewPermission((d) => ({ ...d, openEnded: option.value }));
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
            <div className={styles.addChoices} role="group" aria-label="Goce de sueldo">
              {[
                { label: 'Con goce', value: true },
                { label: 'Sin goce', value: false },
              ].map((option) => (
                <button
                  key={option.label}
                  type="button"
                  aria-pressed={newPermission.withPay === option.value}
                  className={cx(
                    styles.addChoice,
                    newPermission.withPay === option.value && styles.addChoiceOn,
                  )}
                  onClick={() => {
                    setNewPermission((d) => ({ ...d, withPay: option.value }));
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className={cx(styles.addSubmit, styles.addSubmitSuccess, saving && styles.pending)}
              disabled={saving}
              onClick={() => {
                action.run('catalogs', addPermissionKind);
              }}
            >
              Agregar tipo
            </button>
          </div>
          </>
        )}
      </section>

      <section className={cx(styles.panel, styles.panelStacked)}>
        <div className={styles.panelHead}>
          <h2 className={styles.panelTitle}>Motivos de rechazo</h2>
          <Chip background="var(--color-danger-bg)" foreground="var(--color-danger-ink)">
            {catalogs.rejectionReasons.filter((r) => r.enabled).length} de {catalogs.rejectionReasons.length} activos
          </Chip>
        </div>
        <p className={styles.panelNote}>
          Los motivos activos del grupo aparecen como sugerencias en el modal de rechazo; el líder
          siempre puede escribir uno propio.
        </p>

        <div className={styles.catalogScopeTabs} role="tablist" aria-label="Ámbito del motivo">
          {scopes.map((option) => (
            <button
              key={option}
              type="button"
              role="tab"
              aria-selected={scope === option}
              className={cx(styles.catalogScopeTab, scope === option && styles.catalogScopeTabActive)}
              onClick={() => {
                setScope(option);
                if (option !== 'todos') setReasonScope(option);
              }}
            >
              {option === 'todos'
                ? 'Todos'
                : `${rejectionMeta[option].label} · ${catalogs.rejectionReasons.filter((reason) => reason.scope === option && reason.enabled).length}`}
            </button>
          ))}
        </div>

        {reasons.length === 0 ? (
          <div className={styles.emptyRow}>
            Sin motivos sugeridos para este grupo; el líder tendrá que escribirlo a mano.
          </div>
        ) : (
          <ul className={cx(styles.rowsTight, styles.reasonList)}>
            {reasons.map((reason) => (
              <li
                key={reason.id}
                className={cx(styles.switchRow, !reason.enabled && styles.switchRowOff)}
              >
                <span className={cx(styles.rowText, styles.reasonText)}>{reason.text}</span>
                <span className={styles.rowControls}>
                  <Chip
                    background={`color-mix(in srgb, ${rejectionMeta[reason.scope].color} 14%, white)`}
                    foreground={rejectionMeta[reason.scope].color}
                  >
                    {rejectionMeta[reason.scope].label}
                  </Chip>
                  <Toggle
                    on={reason.enabled}
                    color={rejectionMeta[reason.scope].color}
                    label={`${reason.enabled ? 'Retirar' : 'Reactivar'} este motivo`}
                    onToggle={() => {
                      action.run('catalogs', () => toggleReason(reason.id, !reason.enabled));
                    }}
                  />
                  <button
                    type="button"
                    className={cx(styles.removeLink, saving && styles.pending)}
                    disabled={saving}
                    onClick={() => {
                      action.run('catalogs', () =>
                        saveCatalogs(
                          {
                            rejectionReasons: catalogs.rejectionReasons.filter(
                              (item) => item.id !== reason.id,
                            ),
                          },
                          'Motivo eliminado.',
                        ),
                      );
                    }}
                  >
                    Eliminar
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}

        <div className={cx(styles.addForm, styles.reasonAddForm)}>
          <label className={styles.addField}>
            <span className={styles.addLabel}>Nuevo motivo para el grupo elegido</span>
            <input
              type="text"
              className={styles.addInput}
              placeholder="Ej. Ya hay dos personas fuera en la misma célula."
              value={newReason}
              onChange={(e) => {
                setNewReason(e.target.value);
              }}
            />
          </label>
          <button
            type="button"
            className={cx(styles.addSubmit, saving && styles.pending)}
            disabled={saving}
            onClick={() => {
              action.run('catalogs', addReason);
            }}
          >
            Agregar motivo
          </button>
        </div>
      </section>
    </>
  );
};

/**
 * Sample values for the preview's detail table.
 *
 * Presentation fixtures, not configuration: the template's own text is what an
 * administrator edits, and these stand in for the request that would fill the
 * merge tags. Keeping them here rather than in `RuntimeConfig` is the point —
 * nobody should be able to "edit" the example and think they changed the email.
 */
/**
 * Lo que la vista previa dibuja bajo el párrafo.
 *
 * **Las etiquetas son las que el servidor emite de verdad**, y ahí está el punto: esta
 * pantalla existía prometiendo un bloque de detalle que el correo no llevaba, con filas
 * inventadas por evento. Los valores siguen siendo de muestra —no hay una solicitud real
 * que mirar desde aquí— pero la forma es la que llega, fila por fila. Su otra mitad es
 * `SAMPLE_ROWS` en `functions/src/admin/mailTest.ts`, y las dos salen de los `rows:` de
 * cada call site; el límite del paquete impide compartir una sola copia.
 *
 * Cuatro filas del prototipo no están, aquí ni allá: «Traslape», «Vence», «Días de
 * atraso» y «Copia a» describen cosas que este sistema no calcula ni hace.
 */
const PREVIEW_SAMPLE: Readonly<
  Record<EmailEvent, { readonly to: string; readonly rows: readonly (readonly [string, string])[] }>
> = {
  enviada: {
    to: 'mrivas',
    rows: [
      ['Folio', 'VC-0207'],
      ['Periodo', '7 sep – 18 sep 2026 · 10 días hábiles'],
      ['Reemplazo', 'Bruno Chávez'],
      ['Revisa', 'Ana Cifuentes y Luis Ortega'],
    ],
  },
  lider: {
    to: 'acifuentes',
    rows: [
      ['Folio', 'VC-0207'],
      ['Solicita', 'Marco Rivas · Desarrollo iOS'],
      ['Periodo', '7 sep – 18 sep 2026 · 10 días hábiles'],
      ['Reemplazo', 'Bruno Chávez'],
    ],
  },
  reemplazo: {
    to: 'bchavez',
    rows: [
      ['Folio', 'VC-0207'],
      ['Periodo', '7 sep – 18 sep 2026 · 10 días hábiles'],
      ['Cubres a', 'Marco Rivas'],
      ['Pendientes', 'ALPHA-1 · Dejar el pipeline documentado'],
    ],
  },
  respaldo: {
    to: 'mrivas',
    rows: [
      ['Folio', 'VC-0207'],
      ['Periodo', '7 sep – 18 sep 2026'],
      ['Contestó', 'Bruno Chávez'],
      ['Respuesta', 'Puede cubrir el periodo completo'],
    ],
  },
  decision: {
    to: 'mrivas',
    rows: [
      ['Folio', 'VC-0207'],
      ['Estado', 'Aprobada'],
      ['Periodo', '7 sep – 18 sep 2026 · 10 días hábiles'],
      ['Resuelve', 'Ana Cifuentes'],
    ],
  },
  recordatorio: {
    to: 'mrivas',
    rows: [
      ['Folio', 'VC-0207'],
      ['Inicia', '7 sep 2026'],
      ['Último día fuera', '18 sep 2026'],
      ['Regresas', '19 sep 2026'],
      ['Reemplazo', 'Bruno Chávez'],
    ],
  },
  vencimiento: {
    to: 'mrivas',
    rows: [
      ['Por vencer', '5 días'],
      ['Se pierden el', '15 sep 2026'],
      ['Motivo', 'Cierra el periodo de tu aniversario laboral'],
    ],
  },
  imss: {
    to: 'lfernandez',
    rows: [
      ['Folio', 'INC-3402'],
      ['Estado', 'Validada'],
      ['Tipo', 'Enfermedad general'],
      ['Periodo', '7 sep – 13 sep 2026 · 7 días naturales'],
      ['Saldo', 'No se descuenta de tus vacaciones'],
    ],
  },
  resumen: {
    to: 'lideres',
    rows: [
      ['Área', 'Tecnología'],
      ['Semana', '17 ago – 23 ago 2026'],
      ['Fuera', '3 personas'],
      ['Por responder', '2 solicitudes esperando firma'],
    ],
  },
  /* Sin el detalle de lo reportado, y es la fila que falta la que lo dice: el
     qué está mal vive en el portal, no en un correo que se reenvía. */
  datos: {
    to: 'admin',
    rows: [
      ['Quién', 'Marco Rivas'],
      ['Área', 'Tecnología'],
      ['Campos marcados', '2'],
    ],
  },
  reagenda: {
    to: 'mrivas',
    rows: [['Días por reagendar', '5']],
  },
  permiso: {
    to: 'acifuentes',
    rows: [
      ['Solicita', 'Marco Rivas · Desarrollo iOS'],
      ['Folio', 'PER-0018'],
      ['Tipo', 'Salir temprano'],
      ['Horario', 'sale 2 h antes · no regresa'],
      ['Goce', 'Con goce de sueldo'],
    ],
  },
};

/** The merge tags an editor can drop into a subject or body. */
const MERGE_TAGS = ['nombre', 'folio', 'inicio', 'fin', 'lider', 'reemplazo', 'saldo'] as const;

/**
 * Notificaciones por correo.
 *
 * A list of what fires when, and a preview of how it lands. The preview is the
 * part that earns its place: an administrator editing a template is writing an
 * email they will never receive, and a subject line reads differently in a form
 * field than it does above a body.
 *
 * Switching one off is deliberately not the same as deleting it — the movement
 * still reaches the audit log, only the notice stops.
 */
const MailsTab = (): ReactNode => {
  const config = useRuntimeConfig();
  /* Para decir a quién llegaría la prueba, que es quien la está pidiendo. */
  const session = useSession();
  const toast = useToast();
  /*
   * One key for the tab: both writes send `{...config.emailTemplates, [event]:
   * ...}`, every template in one document. Switching one notice off while
   * another is saving would put the other's pre-edit copy back.
   */
  const action = usePendingActions();
  const saving = action.busy;

  const areas = useQuery({
    queryKey: ['system', 'mail', 'areas'],
    queryFn: async () => {
      const result = await getServices().organization.listAreas();
      return result.ok ? result.value.filter((area) => area.active) : [];
    },
  });

  const [selected, setSelected] = useState<EmailEvent>('reemplazo');
  const [mailScope, setMailScope] = useState('Global');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<{ subject: string; body: string; ctaLabel: string } | null>(
    null,
  );

  const templates = EMAIL_EVENTS.map((event) => config.emailTemplates[event]);
  const active = templates.filter((t) => t.enabled).length;
  const current = config.emailTemplates[selected];
  const sample = PREVIEW_SAMPLE[selected];
  /*
   * The sender, read rather than constructed.
   *
   * This used to be `vacaciones@${domain}` — an address assembled from the allowed
   * domain, which looked right and did not exist. With Gmail as the transport the
   * extension ignores its own «Default FROM» and the real sender is whichever account
   * authenticated the SMTP, so nothing the portal can see determines this. It is
   * configuration now, and blank reads as blank: a screen whose job is to say where
   * mail comes from must not guess.
   */
  const domain = config.identity.allowedDomains[0] ?? 'knotion.com';
  const from = config.identity.mailFrom;
  const mailScopes = ['Global', ...(areas.data ?? []).map((area) => area.name)];

  /*
   * The scope tab, as the id the server needs. Empty means the base template, which
   * is what `templateFor` resolves to when no per-area override matches — so a test
   * rehearses exactly the text the selected tab is editing, override included.
   */
  const scopeAreaId =
    mailScope === 'Global'
      ? ''
      : ((areas.data ?? []).find((area) => area.name === mailScope)?.id ?? '');

  /* What the preview renders: the unsaved draft while editing, else the saved template. */
  const shown = draft ?? {
    subject: current.subject,
    body: current.body,
    ctaLabel: current.ctaLabel,
  };

  const write = async (
    patch: Partial<EmailTemplate>,
    message: string,
  ): Promise<boolean> => {
    const result = await getServices().config.save({
      emailTemplates: { ...config.emailTemplates, [selected]: { ...current, ...patch } },
    });
    if (!result.ok) {
      toast.bad(`No se pudo guardar la plantilla.${reasonFor(result.error)}`);
      return false;
    }
    toast.ok(message);
    return true;
  };

  const toggle = async (event: EmailEvent, next: boolean): Promise<void> => {
    const template = config.emailTemplates[event];
    const result = await getServices().config.save({
      emailTemplates: { ...config.emailTemplates, [event]: { ...template, enabled: next } },
    });
    if (!result.ok) {
      toast.bad(`No se pudo guardar la plantilla.${reasonFor(result.error)}`);
      return;
    }
    toast.ok(
      next
        ? `${template.subject}: se enviará a ${template.audience}.`
        : `${template.subject}: nadie recibirá este correo.`,
    );
  };

  return (
    <>
      {/* The screen header already carries the title and subtitle; this strip
          adds only what belongs to this tab — the sender and its two actions. */}
      <div className={styles.mailBar}>
        <div className={styles.mailHeadActions}>
          <Chip background="var(--color-brand-bg-alt)" foreground="var(--color-brand-ink)">
            {active} de {templates.length} activos
          </Chip>
          <button
            type="button"
            className={styles.mailTest}
            disabled={saving}
            onClick={() => {
              action.run('mail-test', async () => {
                const result = await getServices().config.sendTest(selected, scopeAreaId);
                if (!result.ok) {
                  toast.bad(`No se pudo encolar el correo de prueba.${reasonFor(result.error)}`);
                  return;
                }
                /*
                 * «Encolado», never «enviado», and the wording is the whole point of
                 * this button existing. The document is written here and the Trigger
                 * Email extension delivers it afterwards, so a success at this layer
                 * says nothing about an inbox — between 5 and 14 August thirty-eight
                 * notices sat queued and undelivered while every screen reported
                 * success. Naming the recipient is what makes the claim checkable.
                 */
                toast.ok(
                  `«${current.subject}» encolado para ${result.value.recipient}. Si no llega, la entrega es lo que falla, no el portal.`,
                );
              });
            }}
          >
            {saving ? 'Encolando…' : 'Enviar prueba'}
          </button>
        </div>
      </div>

      <div className={styles.mailSplit}>
        <section className={styles.panel}>
          <h3 className={styles.mailListTitle}>Correos del flujo</h3>
          <div className={styles.mailScopeTabs} role="tablist" aria-label="Ámbito de las plantillas">
            {mailScopes.map((scope) => (
              <button
                key={scope}
                type="button"
                role="tab"
                aria-selected={mailScope === scope}
                className={cx(styles.mailScopeTab, mailScope === scope && styles.mailScopeTabActive)}
                onClick={() => {
                  setMailScope(scope);
                }}
              >
                {scope}
              </button>
            ))}
          </div>
          <p className={styles.panelNote}>
            {mailScope === 'Global'
              ? 'Editas la plantilla base: la usan todas las áreas que no tengan texto propio.'
              : `Editas solo la versión de ${mailScope}. Las demás áreas siguen usando la plantilla global.`}
          </p>

          <ul className={styles.mailList}>
            {templates.map((template) => {
              const isSelected = template.event === selected;
              return (
                <li key={template.event}>
                  <button
                    type="button"
                    className={cx(
                      styles.mailRow,
                      isSelected && styles.mailRowSelected,
                      !template.enabled && styles.mailRowOff,
                    )}
                    style={cssVars({ '--mail-color': template.color })}
                    aria-pressed={isSelected}
                    onClick={() => {
                      setSelected(template.event);
                      setDraft(null);
                      setEditing(false);
                    }}
                  >
                    <span className={styles.mailRowMain}>
                      <span className={styles.mailGlyph} aria-hidden="true">
                        {template.glyph}
                      </span>
                      <span className={styles.mailRowText}>
                        <span className={styles.mailRowHead}>
                          <span className={styles.mailEvent}>{eventName(template.event)}</span>
                          {isSelected && (
                            <span className={styles.mailSelectedTag}>En vista previa</span>
                          )}
                        </span>
                        <span className={styles.mailSubject}>{template.subject}</span>
                      </span>
                    </span>
                    <span className={styles.mailRowBottom}>
                      <span className={styles.mailMetaTags}>
                        <Chip
                          background="var(--color-surface-subtle-alt)"
                          foreground="var(--color-text-secondary)"
                        >
                          {template.audience}
                        </Chip>
                        <Chip
                          background="color-mix(in srgb, var(--mail-color) 14%, white)"
                          foreground="var(--mail-color)"
                        >
                          {template.timing}
                        </Chip>
                      </span>
                    </span>
                  </button>
                  {/* Outside the selecting button: a control inside a control
                      cannot be reached by keyboard or read correctly. */}
                  <button
                    type="button"
                    className={cx(
                      styles.mailState,
                      template.enabled ? styles.mailStateOn : styles.mailStateOff,
                      saving && styles.pending,
                    )}
                    disabled={saving}
                    onClick={() => {
                      action.run('mails', () => toggle(template.event, !template.enabled));
                    }}
                  >
                    {saving ? 'Guardando…' : template.enabled ? 'Activo' : 'Inactivo'}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>

        <div className={styles.mailAside}>
          <section className={styles.panel}>
            <div className={styles.mailPreviewHead}>
              <span
                className={styles.mailPreviewGlyph}
                style={cssVars({ '--mail-color': current.color })}
                aria-hidden="true"
              >
                {current.glyph}
              </span>
              <div className={styles.mailPreviewHeadText}>
                <h3 className={styles.mailListTitle}>Vista previa</h3>
                <p className={styles.panelNote}>
                  Así llega el correo de {eventName(selected).toLowerCase()} · {mailScope}.
                  Toca cualquier correo de la lista para ver cómo llega.
                </p>
              </div>
              <div className={styles.mailHeadActions}>
                <Chip
                  background={
                    current.enabled ? 'var(--color-success-bg)' : 'var(--color-danger-bg)'
                  }
                  foreground={
                    current.enabled ? 'var(--color-success-ink)' : 'var(--color-danger-ink)'
                  }
                >
                  {current.enabled ? 'Plantilla activa' : 'Plantilla apagada'}
                </Chip>
                <button
                  type="button"
                  className={styles.mailEdit}
                  onClick={() => {
                    setEditing((was) => !was);
                    if (editing) setDraft(null);
                  }}
                >
                  {editing ? 'Terminar edición' : 'Editar texto'}
                </button>
              </div>
            </div>

            <div className={styles.mailMeta}>
              <Chip
                background="var(--color-surface-subtle-alt)"
                foreground="var(--color-text-secondary)"
              >
                Para: {current.audience}
              </Chip>
              <Chip
                background="var(--color-surface-subtle-alt)"
                foreground="var(--color-text-secondary)"
              >
                Envío: {current.timing}
              </Chip>
            </div>

            {editing && (
              <div className={styles.mailEditor}>
                <label className={styles.tierField}>
                  <span className={styles.tierFieldLabel}>Asunto</span>
                  <input
                    type="text"
                    className={styles.tierInput}
                    value={shown.subject}
                    onChange={(event) => {
                      setDraft({ ...shown, subject: event.target.value });
                    }}
                  />
                </label>
                <label className={styles.tierField}>
                  <span className={styles.tierFieldLabel}>Cuerpo del correo</span>
                  <textarea
                    className={styles.addTextarea}
                    rows={5}
                    value={shown.body}
                    onChange={(event) => {
                      setDraft({ ...shown, body: event.target.value });
                    }}
                  />
                </label>

                <div className={styles.mailTagRow}>
                  <span className={styles.tierFieldLabel}>Insertar dato</span>
                  <div className={styles.addChoices}>
                    {MERGE_TAGS.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        className={styles.addChoice}
                        onClick={() => {
                          setDraft({ ...shown, body: `${shown.body}{{${tag}}}` });
                        }}
                      >
                        {`{{${tag}}}`}
                      </button>
                    ))}
                  </div>
                </div>

                <label className={styles.tierField}>
                  <span className={styles.tierFieldLabel}>Texto del botón</span>
                  <input
                    type="text"
                    className={styles.tierInput}
                    value={shown.ctaLabel}
                    onChange={(event) => {
                      setDraft({ ...shown, ctaLabel: event.target.value });
                    }}
                  />
                </label>

                <div className={styles.mailEditorActions}>
                  <button
                    type="button"
                    className={cx(styles.tierAdd, saving && styles.pending)}
                    disabled={draft === null || saving}
                    onClick={() => {
                      action.run('mails', async () => {
                        if (draft === null) return;
                        const saved = await write(draft, 'Plantilla guardada.');
                        if (saved) setDraft(null);
                      });
                    }}
                  >
                    {saving ? 'Guardando…' : 'Guardar plantilla'}
                  </button>
                  <button
                    type="button"
                    className={styles.tierRemove}
                    disabled={draft === null || saving}
                    onClick={() => {
                      setDraft(null);
                    }}
                  >
                    Descartar cambios
                  </button>
                  <span className={styles.mailHint}>Los cambios se ven al instante abajo.</span>
                </div>
              </div>
            )}

            <div className={styles.mailFrame}>
              <div className={styles.mailFrameHead}>
                <div className={styles.mailAddr}>
                  De: {from.length > 0 ? from : 'sin configurar — Identidad › Remitente'}
                </div>
                {/*
                  Quien recibiría esta prueba, que es quien está mirando.

                  Era `mrivas@knotion.com`, una muestra fija en el código, mientras
                  `sendTestEmail` manda a `caller.email`. Dos afirmaciones distintas
                  sobre la misma pantalla: la de arriba anunciaba un remitente que no
                  gobernaba nada y ésta un destinatario que no era el real. La ficha
                  «Para: {audiencia}» de más arriba sigue diciendo a quién va el aviso
                  de verdad; esta línea es la del ensayo.
                */}
                <div className={styles.mailAddr}>
                  Para: {session.email.length > 0 ? session.email : `${sample.to}@${domain}`}
                </div>
                <div className={styles.mailFrameSubject}>{shown.subject}</div>
              </div>

              <div className={styles.mailFrameBody}>
                <div className={styles.mailBrand}>
                  <span className={styles.mailBrandMark} aria-hidden="true">
                    <img src={logoUrl} alt="" className={styles.mailLogoImage} />
                  </span>
                  {PRODUCT_NAME}
                </div>

                <p className={styles.mailBodyText}>{shown.body}</p>

                <dl className={styles.mailRows}>
                  {sample.rows.map(([key, value]) => (
                    <div key={key} className={styles.mailRowsRow}>
                      <dt className={styles.mailRowsKey}>{key}</dt>
                      <dd className={styles.mailRowsValue}>{value}</dd>
                    </div>
                  ))}
                </dl>

                <span className={styles.mailCta}>{shown.ctaLabel}</span>

                <p className={styles.mailFoot}>
                  Recibes este correo porque participas en el flujo de vacaciones de{' '}
                  {config.identity.commercialName}. Ajusta tus avisos en Mi perfil.
                </p>
              </div>
            </div>
          </section>

          <section className={styles.panel}>
            <h3 className={styles.mailListTitle}>Entrega de los últimos 30 días</h3>
            {/*
              Only the figure this app actually knows. The rest — enviados,
              entregados, abiertos, rebotados — belong to the mail provider, and
              printing invented numbers next to a real one would make the real
              one untrustworthy too.
            */}
            <ul className={styles.healthList}>
              <li className={styles.healthRow}>
                <span
                  className={styles.healthDot}
                  style={cssVars({
                    '--health-color':
                      templates.length - active > 0
                        ? 'var(--color-warning)'
                        : 'var(--color-success)',
                  })}
                  aria-hidden="true"
                />
                <span className={styles.healthLabel}>Plantillas inactivas</span>
                <span className={styles.healthValue}>{templates.length - active}</span>
              </li>
            </ul>
            <p className={styles.panelFootnote}>
              Enviados, entregados, abiertos y rebotados los reporta el proveedor de correo; se
              llenarán cuando esté conectado.
            </p>
          </section>
        </div>
      </div>
    </>
  );
};

/**
 * The handoff's name for each event, distinct from its subject line. The two that do
 * not come from the handoff are named in its register — see `EMAIL_EVENTS`.
 */
const eventName = (event: EmailEvent): string =>
  ({
    enviada: 'Solicitud enviada',
    lider: 'Aviso al líder',
    reemplazo: 'Designación de reemplazo',
    respaldo: 'Respuesta del respaldo',
    decision: 'Aprobación o rechazo',
    recordatorio: 'Recordatorio previo',
    vencimiento: 'Días por vencer',
    imss: 'Incapacidad validada',
    resumen: 'Resumen semanal del área',
    datos: 'Datos reportados como incorrectos',
    reagenda: 'Días devueltos para reagendar',
    permiso: 'Aviso al líder de un permiso',
  })[event];
